// =====================================================================
//  PONT CAPACITOR  —  ce qui differe entre le SITE et l'APP Android
// =====================================================================
//  Ce fichier est charge en PREMIER sur chaque page, avant config.js.
//  Sur le site, il ne fait rien du tout : window.Capacitor n'existe pas,
//  GF_NATIF vaut false, et tout le reste de l'app se comporte comme avant.
//
//  Dans l'APK, trois choses ne peuvent pas fonctionner comme sur le web :
//
//   1. La connexion Google. Google REFUSE OAuth dans une WebView (erreur
//      "disallowed_useragent"). Il faut ouvrir le navigateur systeme, puis
//      revenir dans l'app par un lien profond (deep link).
//   2. Les notifications. Le Web Push (VAPID + PushManager) n'existe pas
//      dans une app Android : on passe par FCM et un jeton d'appareil.
//   3. Le service worker. Inutile ici (les fichiers sont deja sur le
//      telephone) et nuisible : il resservirait d'anciennes versions.
//
//  Aucun bundler n'est necessaire : les plugins Capacitor sont accessibles
//  via window.Capacitor.Plugins, expose par le pont natif au demarrage.
// =====================================================================

const GF_NATIF = !!(window.Capacitor && typeof window.Capacitor.isNativePlatform === "function"
  && window.Capacitor.isNativePlatform());

// L'identifiant de l'application, ecrit UNE fois. Il sert au schema de retour de la connexion
// Google ET au lien vers la page d'abonnement du Play Store : deux litteraux identiques a tenir
// d'accord, c'est un de trop.
// ⛔ Doit correspondre exactement a l'`appId` de capacitor.config.json et au `applicationId` du
// build.gradle : c'est Google qui refuse, pas nous, et son message ne nomme pas la cause.
const GF_APP_ID = "com.besutoriven.gestionfinanciere";

// Adresse de retour apres la connexion Google. Doit etre declaree a l'IDENTIQUE dans
// Supabase (Authentication > URL Configuration > Redirect URLs), sinon Supabase refuse
// la redirection.
const GF_REDIRECT_NATIF = GF_APP_ID + "://auth";

function gfPlugin(nom) {
  return (window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins[nom]) || null;
}

// Marqueur CSS pour les rares ajustements purement visuels de l'app (cf. app.css : masquage des
// barres de defilement). Pose des l'analyse du script, avant le premier rendu : attendre
// DOMContentLoaded produirait un bref clignotement du style web au lancement.
if (GF_NATIF) document.documentElement.classList.add("natif");

// ---------------------------------------------------------------------
//  1. Connexion Google
// ---------------------------------------------------------------------
// Sur le web, signInWithOAuth redirige l'onglet courant et Supabase recupere la session
// tout seul au retour (detectSessionInUrl). Dans l'app, rien de tout ca : on ouvre une
// Custom Tab Chrome, et c'est le deep link qui nous ramene le code d'autorisation.
//
// ⛔⛔⛔ UN SEUL DÉPART À LA FOIS, ET CE VERROU EST LA CORRECTION DES « TROIS CLICS » (2026-08-26).
//
// 🔴 SON SIGNALEMENT : « je suis obligé de cliquer trois fois sur la connexion Google pour que cela
// fonctionne, c'est pas possible ». Deux causes qui s'additionnent, et la seconde explique pourquoi
// c'est précisément le TROISIÈME appui qui marchait :
//
//   ① `signInWithOAuth` est un aller-retour RÉSEAU vers Supabase. Pendant ce temps, rien ne bouge à
//      l'écran — le bouton n'était ni désactivé ni marqué. Quelqu'un qui ne voit rien rappuie.
//
//   ②🔴 CHAQUE APPUI FABRIQUE UN NOUVEAU `code_verifier` PKCE QUI ÉCRASE LE PRÉCÉDENT. Trois appuis,
//      trois onglets, mais UN SEUL vérifieur en mémoire : celui du dernier. Le retour d'un onglet
//      ouvert plus tôt ne correspond donc plus, et `exchangeCodeForSession` le refuse. Seul le
//      dernier onglet ouvert peut aboutir — d'où « il faut cliquer trois fois », qui décrivait
//      exactement le mécanisme sans que personne puisse le voir.
//
// 📌 LE VERROU EST ICI, PAS DANS LES BOUTONS. Trois écrans appellent cette fonction (index.html,
// creer-compte.html, et la liaison depuis les Paramètres) : un garde-fou par bouton se serait oublié
// au quatrième (REGLE.md D2 — on écrit la vraie protection une fois). Les boutons gardent leur
// affichage « en cours », qui règle ① ; ce verrou règle ②, y compris pour un appel qu'on aurait
// oublié de câbler.
// ⚠️ LE DRAPEAU RETOMBE DANS UN `finally`. Sans lui, un refus réseau bloquerait le bouton pour de
// bon, et il faudrait redémarrer l'application pour réessayer — le remède serait pire que le mal.
let _gfAuthEnCours = false;
function gfAuthOccupee() { return _gfAuthEnCours; }

// ⭐⭐ UN LIEN DE CONNEXION QU'ON N'A PAS DEMANDÉ NE CHANGE PAS DE COMPTE (2.32.110, revue du 2026-10-10, sa décision).
// 🔴 Un lien `…://auth#access_token=…&refresh_token=…` (flux implicite) installait la session qu'il porte, MÊME sur un
// téléphone déjà connecté : quelqu'un pouvait faire toucher un lien piégé et basculer l'application sur SON compte — la
// personne y aurait noté ses dépenses sans s'en rendre compte. Le flux normal (PKCE, `?code=`) n'est pas concerné : un
// code ne s'échange qu'avec le vérifieur gardé sur CE téléphone.
// ✅ Les jetons d'un lien ne sont acceptés que si personne n'est connecté, ou si on vient soi-même de demander une
// connexion ou une liaison Google (marque posée juste avant d'ouvrir le navigateur, valable 30 minutes).
// 📌 localStorage et non sessionStorage : Android peut tuer l'application pendant que le navigateur est ouvert.
const GF_CLE_AUTH_DEMANDEE = "gf_auth_demandee";
const GF_AUTH_DEMANDEE_MS = 30 * 60 * 1000;
function gfMarquerAuthDemandee() { try { localStorage.setItem(GF_CLE_AUTH_DEMANDEE, String(Date.now())); } catch (e) {} }
function gfAuthDemandeeRecemment() {
  try {
    const t = Number(localStorage.getItem(GF_CLE_AUTH_DEMANDEE) || 0);
    return t > 0 && Date.now() - t >= 0 && Date.now() - t < GF_AUTH_DEMANDEE_MS;
  } catch (e) { return false; }
}
async function gfJetonsDeLienAcceptables() {
  if (gfAuthDemandeeRecemment()) return true;
  try {
    const vu = await sb.auth.getSession();
    return !(vu && vu.data && vu.data.session);
  } catch (e) { return false; }   // session illisible : dans le doute, on ne remplace rien
}

async function gfConnexionGoogleNative() {
  const Browser = gfPlugin("Browser");
  if (!Browser) throw new Error("Plugin Browser indisponible.");
  if (_gfAuthEnCours) return;   // un départ est déjà en vol : ne pas écraser son code_verifier
  _gfAuthEnCours = true;
  try {
    const { data, error } = await sb.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: GF_REDIRECT_NATIF, skipBrowserRedirect: true },
    });
    if (error) throw error;
    if (!data || !data.url) throw new Error("URL de connexion Google absente.");
    gfMarquerAuthDemandee();
    await Browser.open({ url: data.url, presentationStyle: "popover" });
  } finally {
    _gfAuthEnCours = false;
  }
}

// ⭐ LIER UN COMPTE GOOGLE À UN COMPTE DÉJÀ CONNECTÉ (2026-08-16, sa demande de liaison ; corrigé le
// 2026-08-17 pour l'APK). `profil.html` appelait `sb.auth.linkIdentity` directement, avec un simple
// `redirectTo` web — exactement l'erreur que `gfConnexionGoogleNative` existe pour éviter : Google
// REFUSE OAuth dans une WebView ("disallowed_useragent"). Le bouton fonctionnait donc sur le site,
// mais aurait échoué dans l'APK sans jamais le dire clairement. Même remède : Custom Tab + deep link.
// 🔎 REPÈRES DE DIAGNOSTIC (2026-08-23, son signalement répété deux fois : « aucune réaction » après
// avoir choisi son compte Google — un premier correctif, sur l'identité relue après coup, n'a pas
// suffi). Sans jeton de gestion Supabase ni vrai aller-retour OAuth Google accessibles depuis cet
// environnement, impossible d'observer où le flux s'arrête réellement. Ces repères, posés dans le MÊME
// journal technique qu'il colle déjà à chaque signalement (`noterErreur`, morceau 01), transformeront
// « aucune réaction » en un point précis la prochaine fois — sans rien changer au comportement normal.
async function gfLierIdentiteGoogleNative() {
  const Browser = gfPlugin("Browser");
  if (!Browser) throw new Error("Plugin Browser indisponible.");
  // ⛔ MÊME VERROU, MÊME RAISON : la liaison écrase le MÊME `code_verifier` que la connexion. Deux
  // départs, d'où qu'ils viennent, et l'un des deux retours est perdu.
  if (_gfAuthEnCours) return;
  _gfAuthEnCours = true;
  try {
  const { data, error } = await sb.auth.linkIdentity({
    provider: "google",
    options: { redirectTo: GF_REDIRECT_NATIF, skipBrowserRedirect: true },
  });
  if (error) {
    if (typeof noterErreur === "function") noterErreur("google-liaison", "linkIdentity a refusé : " + (error.message || error), "");
    throw error;
  }
  if (!data || !data.url) throw new Error("URL de liaison Google absente.");
  if (typeof noterRepere === "function") noterRepere("google-liaison", "URL obtenue, ouverture du navigateur.");
  gfMarquerAuthDemandee();
  await Browser.open({ url: data.url, presentationStyle: "popover" });
  } finally {
    _gfAuthEnCours = false;
  }
}

// Le deep link arrive sous la forme :
//   com.besutoriven.gestionfinanciere://auth?code=...        (flux PKCE, cas normal)
//   com.besutoriven.gestionfinanciere://auth#access_token=... (flux implicite, filet de securite)
// URL() ne parse pas les schemas exotiques de facon fiable selon les moteurs : on decoupe a la main.
function gfParamsDepuisLien(url) {
  const params = {};
  const morceaux = String(url).split(/[?#]/).slice(1);
  for (const morceau of morceaux) {
    for (const [cle, val] of new URLSearchParams(morceau)) params[cle] = val;
  }
  return params;
}

// ⭐ Les refus de Supabase arrivent en anglais et en jargon. Les deux qui comptent ici sont traduits
// avec CE QU'IL FAUT FAIRE — un message qu'on ne comprend pas ne vaut guère mieux que pas de message.
// ⚠️ Tout le reste passe tel quel : inventer une traduction pour un cas inconnu masquerait sa vraie
// nature, et c'est précisément ce qui a fait perdre trois versions sur cette liaison.
function gfMessageLiaison(msg) {
  const m = String(msg || "");
  if (/already linked to another user/i.test(m)) {
    // ⭐ « TRÈS PROBABLEMENT LE VÔTRE » A ÉTÉ AJOUTÉ LE 2026-08-24 (21e session), ET C'EST LA PHRASE QUI
    // MANQUAIT. Son signalement : « ça me met que le compte que j'essaie de lier a déjà un compte
    // Solvea, mais c'est faux ». Le message était exact — vérifié dans `auth.identities` : l'adresse
    // Google qu'il visait était bien l'identité d'un autre compte Solvea. Mais « un autre compte »
    // laisse imaginer quelqu'un d'autre, alors que le cas de LOIN le plus fréquent est un compte
    // qu'on s'est créé soi-même, plus tôt, en touchant « Se connecter avec Google » au lieu de
    // « Créer un compte ». Nommer cette hypothèse, c'est la différence entre un refus incompréhensible
    // et un refus qu'on sait résoudre.
    // ⛔ On ne dit PAS lequel : ce serait révéler l'existence et l'adresse d'un compte à quelqu'un qui
    // n'a fait que présenter une identité Google. Le serveur ne le dit pas non plus, et c'est bien.
    return "Cette adresse Google sert déjà à se connecter à un autre compte Solvea — très probablement "
      + "le vôtre, créé plus tôt avec « Se connecter avec Google ». Connectez-vous à ce compte-là pour "
      + "l'en détacher, ou choisissez une autre adresse Google.";
  }
  if (/identity is already linked/i.test(m)) {
    return "Ce compte Google est déjà lié à votre compte — il n'y a rien de plus à faire.";
  }
  if (/manual linking.*disabled|manual_linking_disabled/i.test(m)) {
    return "La liaison de comptes est désactivée côté serveur. Rien à faire depuis l'application.";
  }
  return m;
}

async function gfTraiterRetourAuth(url) {
  const p = gfParamsDepuisLien(url);
  const Browser = gfPlugin("Browser");
  const estLiaison = (() => { try { return sessionStorage.getItem("gf_reglages_apres_auth") === "1"; } catch (e) { return false; } })();
  // ⭐⭐ LES REPÈRES COUVRENT MAINTENANT LA CONNEXION AUSSI, PAS SEULEMENT LA LIAISON (2026-08-26).
  //
  // 🔴 SON SIGNALEMENT : « je suis obligé de cliquer trois fois sur la connexion Google pour que cela
  // fonctionne ». Un aller-retour OAuth réel ne se rejoue pas depuis l'environnement de développement
  // (ni téléphone, ni Custom Tab, ni compte Google) : ce qui manquait pour le trouver n'était pas une
  // idée de plus, c'était une TRACE. Les repères de la liaison, posés le 2026-08-23, avaient déjà
  // transformé « aucune réaction » en une cause nommée en une seule fois — mais ils étaient sous
  // `estLiaison`, donc muets sur le chemin de la connexion, qui est celui dont il parle.
  // 📌 Le mot-clé change avec le chemin (`google-connexion` / `google-liaison`) pour que le Journal
  // technique dise LEQUEL des deux a échoué : les deux passent par cette fonction.
  const quoi = estLiaison ? "google-liaison" : "google-connexion";
  if (typeof noterRepere === "function") {
    noterRepere(quoi, "Retour reçu — " + (p.error ? "erreur : " + (p.error_description || p.error) : p.code ? "code présent" : (p.access_token ? "jeton implicite présent" : "AUCUN paramètre reconnu")));
  }

  // Erreur renvoyee par Google/Supabase (utilisateur qui annule, compte refuse...).
  if (p.error || p.error_description) {
    if (Browser) { try { await Browser.close(); } catch (e) {} }
    return { ok: false, msg: p.error_description || p.error };
  }

  let erreur = null;
  if (p.code) {
    const { error } = await sb.auth.exchangeCodeForSession(p.code);
    erreur = error;
  } else if (p.access_token && p.refresh_token) {
    if (!(await gfJetonsDeLienAcceptables())) {
      if (typeof noterErreur === "function") noterErreur(quoi, "Lien de connexion NON DEMANDÉ ignoré : une session est déjà ouverte.", "");
      if (Browser) { try { await Browser.close(); } catch (e) {} }
      return { ok: false, msg: "Ce lien de connexion n'a pas été demandé depuis ce téléphone : il a été ignoré, vous restez sur votre compte." };
    }
    const { error } = await sb.auth.setSession({
      access_token: p.access_token,
      refresh_token: p.refresh_token,
    });
    erreur = error;
  } else {
    // ⛔⛔ CE CHEMIN ÉTAIT TOTALEMENT MUET, ET C'EST LA FORME EXACTE DE « RIEN NE SE PASSE ».
    // Il est légitime pour un lien profond sans rapport avec l'authentification — mais si l'adresse
    // porte NOTRE schéma de retour, alors c'est un retour d'auth arrivé SANS code ni jeton : le seul
    // cas où l'on toucherait « Se connecter » et où l'écran ne bougerait pas d'un pixel. On ne peut
    // rien en faire, mais on peut le DIRE au Journal technique (REGLE.md C1 : un silence n'est pas
    // un succès), et c'est ce qui nommera la cause de ses « trois clics ».
    if (String(url).indexOf(GF_REDIRECT_NATIF) === 0 && typeof noterErreur === "function") {
      noterErreur(quoi, "Retour d'auth SANS code ni jeton — paramètres reçus : " + Object.keys(p).join(",") || "(aucun)", "");
    }
    return { ok: false, msg: null }; // lien profond sans rapport avec l'auth : on ignore
  }

  if (typeof noterRepere === "function") {
    noterRepere(quoi, erreur ? "Échange du code refusé : " + (erreur.message || erreur) : "Échange du code réussi.");
  }
  // ⭐⭐ ON CONSTATE LA SESSION AVANT DE NAVIGUER, on ne la suppose pas (2026-08-26).
  //
  // 🔴 POURQUOI C'EST LE PREMIER SUSPECT DES « TROIS CLICS » : juste en dessous, on part sur
  // `app.html`, qui rejoue `exigerConnexion()` à son démarrage. Si la session n'est pas encore
  // LISIBLE au moment de cette navigation, `app.html` renvoie vers l'écran de connexion — et la
  // personne, qui a pourtant choisi son compte Google, se retrouve devant le même bouton. Elle
  // rappuie. Au deuxième ou troisième passage la session est là, et « ça marche ».
  // ⚠️ ON NE CORRIGE RIEN DE FORCE ICI, ET C'EST VOULU : rien ne prouve encore que ce soit la cause.
  // Ce qu'on fait, c'est le CONSTATER et l'écrire. Si le Journal montre « échange réussi » suivi de
  // « session absente », la cause est nommée et le remède devient évident.
  if (!erreur && typeof sb === "object" && sb && sb.auth && typeof sb.auth.getSession === "function") {
    try {
      const vu = await sb.auth.getSession();
      const laSession = vu && vu.data && vu.data.session;
      if (typeof noterRepere === "function") {
        noterRepere(quoi, laSession ? "Session constatée avant la navigation." : "⚠️ Session ABSENTE juste après un échange réussi.");
      }
    } catch (e) {
      if (typeof noterRepere === "function") noterRepere(quoi, "Lecture de la session impossible : " + (e && e.message));
    }
  }
  try { localStorage.removeItem(GF_CLE_AUTH_DEMANDEE); } catch (e) {}   // une demande sert une fois
  if (Browser) { try { await Browser.close(); } catch (e) {} }
  if (erreur) return { ok: false, msg: (typeof messageErreur === "function" ? messageErreur(erreur) : erreur.message) };

  // ⭐ TOUJOURS app.html (2026-08-17) — que ce soit une CONNEXION ou une LIAISON lancée depuis les
  // Paramètres. `profil.html` a disparu (fusionné dans app.html) : une liaison n'a plus de page à
  // part où revenir spécifiquement. `gf_reglages_apres_auth` (posé avant d'ouvrir le navigateur,
  // voir le morceau 18) est lu par app.html LUI-MÊME à son démarrage pour rouvrir les Paramètres —
  // ce fichier n'a plus besoin de choisir une destination différente, une seule existe désormais.
  // exigerMFA() est rejoue par app.html au chargement : un compte avec 2FA sera bloque la,
  // exactement comme apres une connexion Google sur le site.
  window.location.href = "app.html";
  return { ok: true };
}

// ---------------------------------------------------------------------
//  2. Notifications (FCM)
// ---------------------------------------------------------------------
// Cote base, une ligne push_subscriptions sert aux deux mondes : le web y range son endpoint
// VAPID, l'app y range son jeton FCM avec platform='fcm' (cf. migration push_fcm.sql).
const GF_CLE_JETON_FCM = "gf_fcm_token";

function gfJetonFcmLocal() {
  try { return localStorage.getItem(GF_CLE_JETON_FCM); } catch (e) { return null; }
}

// register() est asynchrone cote natif : le jeton n'arrive pas en valeur de retour mais par
// l'evenement 'registration'. On enveloppe les deux dans une promesse, avec un delai de garde
// pour ne pas laisser l'interface figee si le service Google ne repond jamais.
function gfAttendreJetonFcm(PN) {
  return new Promise((resolve, reject) => {
    let fini = false;
    const minuteur = setTimeout(() => {
      if (fini) return;
      fini = true;
      reject(new Error("Le service de notifications Google n'a pas repondu. Verifie votre connexion, puis reessaie."));
    }, 20000);

    PN.addListener("registration", (jeton) => {
      if (fini) return;
      fini = true;
      clearTimeout(minuteur);
      resolve(jeton && jeton.value);
    });
    PN.addListener("registrationError", (err) => {
      if (fini) return;
      fini = true;
      clearTimeout(minuteur);
      reject(new Error((err && err.error) || "Enregistrement FCM refuse."));
    });
    PN.register().catch((e) => {
      if (fini) return;
      fini = true;
      clearTimeout(minuteur);
      reject(e);
    });
  });
}

async function gfActiverPushNatif(uid) {
  const PN = gfPlugin("PushNotifications");
  if (!PN) return { ok: false, msg: "Notifications indisponibles sur cet appareil." };

  let perm = await PN.checkPermissions();
  if (perm.receive === "prompt" || perm.receive === "prompt-with-rationale") {
    perm = await PN.requestPermissions();
  }
  if (perm.receive !== "granted") {
    return { ok: false, msg: "Autorisation refusee. Activez les notifications pour l'app dans les reglages Android." };
  }

  try {
    const jeton = await gfAttendreJetonFcm(PN);
    if (!jeton) return { ok: false, msg: "Jeton de notification vide." };

    const { error } = await sb.from("push_subscriptions").upsert(
      { user_id: uid, endpoint: "fcm:" + jeton, p256dh: null, auth: null, platform: "fcm" },
      { onConflict: "user_id,endpoint" },
    );
    if (error) return { ok: false, msg: "Erreur d'enregistrement : " + error.message };

    try { localStorage.setItem(GF_CLE_JETON_FCM, jeton); } catch (e) {}
    return { ok: true };
  } catch (e) {
    const brut = String((e && e.message) || e);
    try {
      localStorage.setItem("gf_notif_derniere_erreur", JSON.stringify({ msg: brut, quand: new Date().toISOString() }));
    } catch (e2) {}
    return { ok: false, msg: brut };
  }
}

async function gfDesactiverPushNatif(uid) {
  const jeton = gfJetonFcmLocal();
  if (!jeton) return;
  try { await sb.from("push_subscriptions").delete().eq("user_id", uid).eq("endpoint", "fcm:" + jeton); } catch (e) {}
  try { localStorage.removeItem(GF_CLE_JETON_FCM); } catch (e) {}
}

// Le pendant natif de abonnementPushActuel() : l'app n'a pas de PushManager a interroger, donc
// on se fie au jeton garde en local, confirme par la presence de la ligne en base.
async function gfPushNatifActif(uid) {
  const jeton = gfJetonFcmLocal();
  if (!jeton || !uid) return false;
  try {
    const { data } = await sb.from("push_subscriptions").select("id").eq("user_id", uid)
      .eq("endpoint", "fcm:" + jeton).limit(1);
    return !!(data && data.length);
  } catch (e) {
    return false;
  }
}

// ---------------------------------------------------------------------
//  3. Deverrouillage par empreinte ou visage
// ---------------------------------------------------------------------
//  Deux mondes, une seule promesse pour le reste de l'app :
//    - dans l'APK : le plugin @aparajita/capacitor-biometric-auth, qui affiche
//      le dialogue systeme d'Android (empreinte, visage, ou schema de l'ecran) ;
//    - sur le web : WebAuthn, la norme du navigateur. Le meme geste, sans plugin.
//
//  Ce que ce verrou est, et n'est pas : une porte d'entree LOCALE, propre a
//  l'appareil, exactement comme le code a 4 chiffres qu'il complete. Il ne
//  protege pas les donnees cote serveur — c'est la session Supabase qui s'en
//  charge. On ne cherche donc pas a lier la biometrie a un secret : reussir
//  l'authentification suffit, et le code reste toujours disponible en secours.

const GF_BIO_CLE = "gf_bio_webauthn"; // identifiant WebAuthn memorise (web uniquement)

async function gfBiometrieDisponible() {
  if (GF_NATIF) {
    const B = gfPlugin("BiometricAuthNative");
    if (!B || !B.checkBiometry) return false;
    try {
      const r = await B.checkBiometry();
      return !!(r && (r.isAvailable || r.strongBiometryIsAvailable));
    } catch (e) { return false; }
  }
  // Web : il faut un authentificateur integre a l'appareil (Touch ID, Windows Hello,
  // empreinte Android). Une cle USB ferait techniquement l'affaire mais n'a aucun sens ici.
  if (!window.PublicKeyCredential || !window.isSecureContext) return false;
  try {
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch (e) { return false; }
}

// Enregistrement, uniquement necessaire sur le web : Android n'a rien a memoriser.
async function gfBiometrieActiver() {
  if (GF_NATIF) return await gfBiometrieDisponible();
  if (!(await gfBiometrieDisponible())) return false;
  try {
    const defi = new Uint8Array(32); crypto.getRandomValues(defi);
    const idUtilisateur = new Uint8Array(16); crypto.getRandomValues(idUtilisateur);
    const cred = await navigator.credentials.create({
      publicKey: {
        challenge: defi,
        rp: { name: "Solvea" },
        user: { id: idUtilisateur, name: "solvea-local", displayName: "Solvea" },
        pubKeyCredParams: [{ type: "public-key", alg: -7 }, { type: "public-key", alg: -257 }],
        authenticatorSelection: { authenticatorAttachment: "platform", userVerification: "required", residentKey: "preferred" },
        timeout: 60000,
        attestation: "none",
      },
    });
    if (!cred) return false;
    localStorage.setItem(GF_BIO_CLE, btoa(String.fromCharCode(...new Uint8Array(cred.rawId))));
    return true;
  } catch (e) { return false; }
}

function gfBiometrieDesactiver() { try { localStorage.removeItem(GF_BIO_CLE); } catch (e) {} }

// Rend true si l'utilisateur s'est authentifie, false dans tous les autres cas (refus, echec,
// materiel indisponible). L'appelant retombe alors sur le code a 4 chiffres — jamais de blocage.
// Garde la trace du dernier echec, comme gf_notif_derniere_erreur pour les notifications : sans ca
// un capteur qui refuse est indistinguable d'un reglage jamais active — c'est exactement le piege
// dans lequel on est tombe avec internalAuthenticate (cf. ci-dessous).
function gfBioNoterErreur(e) {
  try {
    localStorage.setItem("gf_bio_derniere_erreur", JSON.stringify({
      msg: String((e && e.message) || e || "inconnu"),
      code: (e && e.code) || null,
      quand: new Date().toISOString(),
    }));
  } catch (e2) {}
}

async function gfBiometrieVerifier(raison) {
  if (GF_NATIF) {
    const B = gfPlugin("BiometricAuthNative");
    // ATTENTION — le pont natif de Capacitor n'expose QUE les methodes declarees @PluginMethod
    // cote Java (JSExport.createPluginObj) : ici checkBiometry et **internalAuthenticate**.
    // `authenticate()` n'existe que dans le wrapper JS du plugin (dist/esm/base.js), que nous ne
    // chargeons pas puisqu'il n'y a aucun bundler. Appeler B.authenticate donnait donc undefined,
    // et le verrou retombait silencieusement sur le code a 4 chiffres : la biometrie n'a jamais
    // ete demandee dans l'APK (bug constate sur telephone le 2026-08-07).
    const authentifier = B && (B.internalAuthenticate || B.authenticate);
    if (!authentifier) { gfBioNoterErreur("methode d'authentification absente du plugin"); return false; }
    try {
      // ⚠️ Bug remonte le 2026-08-08 : `reason` devient la DESCRIPTION du dialogue Android (voir
      // AuthActivity.java du plugin : title/subtitle/description = androidTitle/androidSubtitle/reason), et on
      // posait la MEME phrase dans `reason` ET `androidSubtitle` — deux lignes identiques a l'ecran. Une seule
      // des deux suffit ; `androidTitle` porte deja le nom de l'app, `reason` porte l'instruction.
      await authentifier.call(B, {
        reason: raison || "Déverrouille l'application",
        cancelTitle: "Utiliser mon code",
        androidTitle: "Solvea",
        allowDeviceCredential: false, // le code de l'app reste notre secours, pas celui du telephone
      });
      return true;
    } catch (e) { gfBioNoterErreur(e); return false; }
  }
  const brut = (() => { try { return localStorage.getItem(GF_BIO_CLE); } catch (e) { return null; } })();
  if (!brut) return false;
  try {
    const rawId = Uint8Array.from(atob(brut), c => c.charCodeAt(0));
    const defi = new Uint8Array(32); crypto.getRandomValues(defi);
    const ass = await navigator.credentials.get({
      publicKey: {
        challenge: defi,
        allowCredentials: [{ type: "public-key", id: rawId, transports: ["internal"] }],
        userVerification: "required",
        timeout: 60000,
      },
    });
    return !!ass;
  } catch (e) { gfBioNoterErreur(e); return false; }
}

// ---------------------------------------------------------------------
//  4. Impression et enregistrement de fichiers
// ---------------------------------------------------------------------
// Deux comportements du navigateur dont l'APK n'a pas d'equivalent natif :
//   - window.print() ne fait RIEN dans une WebView Capacitor (bouton "Generer le PDF" mort, signale
//     le 2026-08-09) — corrige par un petit plugin maison, PrintWebViewPlugin.java, qui ouvre le
//     service d'impression du systeme (toujours capable d'"Enregistrer en PDF"). Voir ce fichier
//     pour le detail : c'est la methode officiellement documentee par Android pour imprimer une
//     WebView, pas un contournement ;
//   - un <a download> sur une URL blob: ne declenche aucun telechargement (pas de gestionnaire de
//     telechargement branche sur ce schema dans une WebView nue) — bouton "Telecharger la
//     sauvegarde" mort pour la meme raison. Corrige en ecrivant le fichier dans le cache de l'appli
//     (Filesystem, aucune permission de stockage requise) puis en proposant son partage (Share) :
//     l'utilisateur choisit "Enregistrer dans Fichiers", l'envoyer par e-mail, etc.
// Sur le web/PWA, rien de tout ceci ne s'execute (GF_NATIF vaut false) : le comportement par blob +
// <a download> qui fonctionne deja dans un navigateur reste inchange.

async function gfImprimerPage() {
  const PrintWebView = gfPlugin("PrintWebView");
  if (PrintWebView) { await PrintWebView.print(); return; }
  window.print(); // secours si le plugin est absent pour une raison ou une autre
}

function gfBlobEnBase64(blob) {
  return new Promise((resolve, reject) => {
    const lecteur = new FileReader();
    lecteur.onload = () => resolve(String(lecteur.result).split(",")[1] || "");
    lecteur.onerror = () => reject(lecteur.error);
    lecteur.readAsDataURL(blob);
  });
}

// ⭐ TELECHARGER, ET NON PARTAGER (2026-08-10, sa demande). "Telecharger la sauvegarde" ouvrait la
// feuille de partage : il fallait designer une application a qui ENVOYER le fichier pour arriver a
// l'enregistrer. Ce n'est pas le meme geste. SaveFilePlugin (plugin natif du depot) ouvre le vrai
// selecteur de fichiers d'Android — dossier Telechargements en tete, bouton "Enregistrer" — qui est
// la methode officielle pour ecrire un fichier choisi par l'utilisateur, sans aucune permission de
// stockage a demander.
//
// Renvoie { annule:true } si la personne referme le selecteur : ANNULER N'EST PAS UNE ERREUR, et
// l'appelant ne doit rien afficher en rouge dans ce cas.
// Repli sur le partage si le plugin manque (vieil APK installe par-dessus, par exemple) : mieux vaut
// l'ancien comportement que rien du tout.
// 🔴🔴🔴 LE CONTENU NE TRAVERSE PLUS LE SELECTEUR DE FICHIERS (2026-08-19).
//
// Ses sauvegardes du 11 et du 16 aout pesaient **0 octet** ; celle du 10 aout, ecrite par l'ancien
// chemin, pesait 182 ko. Le filet de securite du projet n'attrapait donc plus rien depuis la creation
// de SaveFilePlugin — et l'application annoncait « Sauvegarde enregistree ».
//
// LA CAUSE : les 250 ko de base64 etaient passes a `SaveFile.save()`, puis RELUS apres le retour du
// selecteur de fichiers. Or DocumentsUI est un processus lourd et notre activite porte une WebView :
// pendant que le selecteur est au premier plan, Android tue tres volontiers le notre. Au retour,
// l'appel revient sans ses donnees, l'ecriture n'a jamais lieu — mais le fichier a deja ete CREE par
// le selecteur, donc il reste la, a 0 octet, avec l'air d'une sauvegarde.
//
// ⭐ On ecrit donc le contenu dans un fichier TEMPORAIRE d'abord, et seul son CHEMIN traverse le
// retour d'activite. Une chaine de 80 caracteres survit a ce qu'une charge de 250 ko ne survit pas ;
// le plugin copie ensuite un flux vers l'autre, sans limite de taille.
// ⛔ ET ON VERIFIE : le plugin renvoie le nombre d'octets ecrits, et refuse un fichier vide. Une
// sauvegarde qui echoue en silence est PIRE que pas de sauvegarde — on cesse d'en faire d'autres.
// ⭐⭐ CHAQUE ETAPE LAISSE UNE TRACE DANS LE JOURNAL TECHNIQUE (2026-08-30, son image 16 : trois
// sauvegardes a 0 octet, et son journal complet ne disait RIEN sur l'export). Cette fonction
// traverse trois mondes (JS de l'app, pont natif, plugin Java) et aucun ne disait ou il en etait.
// `gfNoterExport` est posee par le morceau 12 : ce fichier vit hors de la portee de l'app.
function gfExport(etape, detail) {
  try { if (typeof window.gfNoterExport === "function") window.gfNoterExport("natif/" + etape, detail); } catch (e) {}
}
/**
 * ⭐⭐⭐ DEUX ROUTES, ET LA PAR DEFAUT NE QUITTE PLUS L'APPLICATION (2026-09-05, son image 13 :
 * « la sauvegarde est toujours exportee a 0 ko »).
 *
 * 🔴 CE QUE SON JOURNAL A PROUVE, ET C'EST LA PREMIERE FOIS QU'ON POUVAIT LE VOIR. Le 5 septembre
 * a 17h44 il a lance deux exports a six secondes d'intervalle :
 *   17:44:23  plugin {"annule":false,"octets":461006,"relus":461006}   ← REUSSI, ET RELU
 *   17:44:26  natif/temporaire : ecrit
 *   17:44:31  plugin {"annule":true}                                   ← et un fichier 0 o reste
 * Le contenu etait bon : le premier export l'a ecrit ET relu en entier. C'est le SELECTEUR DE
 * FICHIERS qui a rendu « annule » apres avoir deja cree le document.
 * ⛔ Trois versions ont colmate cet aller-retour d'activite (contenu dans un temporaire, octets
 * comptes, fichier relu). Chacune a reduit le risque sans le supprimer, parce que le risque EST
 * l'aller-retour : `MainActivity` est en `singleTask`, DocumentsUI se lance dans sa propre tache, et
 * Android tue volontiers un processus qui porte une WebView pendant ce temps.
 *
 * ⭐ SA DECISION : « les deux boutons ». Par defaut, `emplacement:"telechargements"` ecrit
 * directement dans le dossier Telechargements via MediaStore — aucune activite lancee, donc aucune
 * des pannes ci-dessus n'est meme possible. « Choisir l'emplacement » garde le selecteur pour qui
 * veut ranger son fichier ailleurs.
 * ⚠️ AVANT ANDROID 10, MediaStore.Downloads n'existe pas : le plugin repond `INDISPONIBLE` et on
 * repasse par le selecteur, sans rien dire a personne. On ne fait pas semblant d'avoir une route
 * qu'on n'a pas, et on n'echoue pas non plus.
 */
/**
 * ⭐ LA PERMISSION DE NOTIFIER SE DEMANDE AU PREMIER EXPORT, JAMAIS AU DEMARRAGE (2026-09-08).
 * Une application qui reclame ses permissions avant d'avoir montre a quoi elles servent se fait
 * refuser — et Android ne redemande plus jamais apres deux refus.
 * ⛔ Un refus n'empeche rien : le fichier s'ecrit pareil, seule la notification manque. On ne
 * bloque donc jamais l'export la-dessus, et on n'insiste pas.
 */
async function gfDemanderNotifications() {
  try {
    // ⚠️ `Notif` D'ABORD, `SaveFile` EN REPLI (2026-09-09). Le plugin de notification a ete sorti de
    // `SaveFilePlugin` le jour ou l'alerte de depassement en a eu besoin elle aussi. Le repli sert
    // au cas d'un APK plus ancien installe par-dessus : mieux vaut l'ancien chemin que rien.
    const Notif = gfPlugin("Notif");
    if (Notif && Notif.demander) {
      const r = await Notif.demander();
      gfExport("notifications", r && r.accorde ? "accordee" : "refusee");
      return;
    }
    const SaveFile = gfPlugin("SaveFile");
    if (SaveFile && SaveFile.demanderNotifications) {
      const r = await SaveFile.demanderNotifications();
      gfExport("notifications", r && r.accorde ? "accordee" : "refusee");
    }
  } catch (e) { gfExport("notifications/echec", String((e && e.message) || e)); }
}

/**
 * ⭐ POSER UNE NOTIFICATION DEPUIS L'APPLICATION (2026-09-09, pour l'alerte de depassement, A2).
 *
 * ⛔ ELLE NE PEUT PAS FAIRE ECHOUER CE QUI L'APPELLE. Une notification est un confort : si le plugin
 * manque (navigateur web), si la permission est refusee, si le salon est desactive — l'appelant
 * continue exactement pareil. C'est pour ca qu'elle avale tout et ne rend rien.
 * ⚠️ ET ELLE DEMANDE LA PERMISSION AU PREMIER USAGE, jamais au demarrage : une application qui
 * reclame avant d'avoir montre a quoi ca sert se fait refuser, et Android ne redemande plus.
 */
async function gfNotifier(titre, texte) {
  try {
    const Notif = gfPlugin("Notif");
    if (!Notif || !Notif.poser) return false;
    await gfDemanderNotifications();
    const r = await Notif.poser({ titre: titre, texte: texte });
    return !!(r && r.posee);
  } catch (e) { return false; }
}
window.gfNotifier = gfNotifier;

async function gfTelechargerFichier(nomFichier, blob, mime, options) {
  const SaveFile = gfPlugin("SaveFile");
  const Filesystem = gfPlugin("Filesystem");
  const versTelechargements = !options || options.emplacement !== "choisir";
  gfExport("entree", "SaveFile=" + !!SaveFile + " Filesystem=" + !!Filesystem + " taille=" + ((blob && blob.size) || 0)
    + " vers=" + (versTelechargements ? "telechargements" : "selecteur"));
  // Garde-fou tout en amont : on ne propose jamais d'enregistrer un contenu vide.
  if (!blob || !blob.size) throw new Error("Le contenu à enregistrer est vide : rien n'a été écrit.");
  if (SaveFile) {
    // ⚠️ AVANT d'ecrire, pas apres : la notification est posee par le plugin a la fin de l'ecriture,
    // et demander la permission a ce moment-la la ferait arriver trop tard pour le premier export.
    await gfDemanderNotifications();
    const type = mime || blob.type || "application/octet-stream";
    let r;
    if (Filesystem) {
      const base64 = await gfBlobEnBase64(blob);
      gfExport("base64", base64.length + " caracteres");
      if (!base64) throw new Error("Le contenu à enregistrer est vide : rien n'a été écrit.");
      const temporaire = "gf-export-" + Date.now() + "-" + nomFichier;
      await Filesystem.writeFile({ path: temporaire, data: base64, directory: "CACHE" });
      const { uri } = await Filesystem.getUri({ path: temporaire, directory: "CACHE" });
      gfExport("temporaire", uri ? "ecrit" : "URI VIDE");
      // ⭐ LA ROUTE DIRECTE D'ABORD. `telechargements` n'ouvre aucune activite : la panne du selecteur
      // (fichier cree puis « annule ») ne peut pas s'y produire. Elle rejette `INDISPONIBLE` avant
      // Android 10, et c'est le SEUL cas ou l'on repasse par le selecteur sans que la personne l'ait
      // demande. ⛔ Toute autre erreur remonte telle quelle : un echec d'ecriture ne doit pas se
      // transformer en « on va essayer autrement », sinon on masque la vraie panne.
      if (versTelechargements && SaveFile.telechargements) {
        try {
          r = await SaveFile.telechargements({ nom: nomFichier, source: uri, mime: type });
          gfExport("plugin/telechargements", JSON.stringify(r || null));
        } catch (e) {
          const m = String((e && e.message) || e);
          if (m.indexOf("INDISPONIBLE") < 0) { try { await Filesystem.deleteFile({ path: temporaire, directory: "CACHE" }); } catch (x) {} throw e; }
          gfExport("telechargements/indisponible", "Android < 10 : on repasse par le selecteur");
        }
      }
      // ⭐⭐⭐ « CHOISIR L'EMPLACEMENT » PASSE PAR UN DOSSIER, PLUS PAR UN SELECTEUR DE FICHIER
      // (2026-09-08, sa demande : « essaie encore de le reparer »).
      //
      // 🔴 CE QUE LES TROIS TENTATIVES PRECEDENTES N'ONT PAS PU CORRIGER. `SaveFile.save()` ouvre
      // ACTION_CREATE_DOCUMENT : c'est LE SELECTEUR qui cree le fichier, des qu'on appuie sur
      // Enregistrer. Si Android recycle notre processus pendant ce temps — ce qu'il fait volontiers,
      // WebView + DocumentsUI + une activite en singleTask — le resultat revient « annule », le
      // fichier existe deja, vide, et nous n'avons meme pas son URI pour le supprimer.
      // ⭐ `SaveFile.dossier()` demande un DOSSIER (ACTION_OPEN_DOCUMENT_TREE) : rien n'est cree
      // pendant le choix, la permission est PERSISTANTE, et tous les exports suivants ecrivent sans
      // ouvrir la moindre activite. La classe de panne disparait au lieu d'etre evitee.
      // ⛔ On garde `save()` en repli pour un APK plus ancien installe par-dessus : mieux vaut
      // l'ancien comportement, avec ses defauts connus, que rien du tout.
      if (!r && SaveFile.dossier) {
        // ⭐ « Autre dossier… » REDEMANDE toujours le dossier (2.31.168, sa demande : « il faut que l'on puisse choisir
        // quel dossier »). Le dossier choisi devient celui que retient l'app — donc aussi celui de la sauvegarde
        // hebdomadaire, qui, elle, écrit en silence dans le dossier retenu.
        r = await SaveFile.dossier({ nom: nomFichier, source: uri, mime: type, redemander: true });
        gfExport("plugin/dossier", JSON.stringify(r || null));
      }
      if (!r) {
        r = await SaveFile.save({ nom: nomFichier, source: uri, mime: type });
        gfExport("plugin", JSON.stringify(r || null));
      }
      // Le temporaire ne sert plus a rien : le laisser encombrerait le cache a chaque export.
      try { await Filesystem.deleteFile({ path: temporaire, directory: "CACHE" }); } catch (e) {}
    } else {
      // Repli si Filesystem manque : l'ancien chemin, avec la meme verification de taille au retour.
      r = await SaveFile.save({ nom: nomFichier, data: await gfBlobEnBase64(blob), mime: type });
    }
    if (r && r.annule) { gfExport("annule", ""); return { annule: true }; }
    const ecrits = Number((r && r.octets) || 0);
    if (!ecrits) {
      gfExport("VIDE", "le plugin a rendu 0 octet");
      throw new Error("Le fichier enregistré est vide (0 octet) : la sauvegarde a échoué.");
    }
    // ⭐ `relus` EST LA TAILLE CONSTATEE SUR LE FICHIER, pas celle qu'on croit avoir ecrite
    // (2026-09-02, son image 20). -1 = le fournisseur a refuse la relecture, ce qui n'est pas un
    // echec ; toute autre valeur est un fait, et elle part dans le journal telle quelle.
    const relus = (r && r.relus != null) ? Number(r.relus) : -1;
    gfExport("ecrit", ecrits + " octets, relus " + (relus < 0 ? "non verifiable" : relus + " octets")
      + (r && r.dossier ? ", dans " + r.dossier : ""));
    return { annule: false, octets: ecrits, relus: relus, dossier: (r && r.dossier) || null };
  }
  await gfEnregistrerFichier(nomFichier, blob);
  return { annule: false };
}

// =====================================================================================
// ⭐⭐ LA SAUVEGARDE HEBDOMADAIRE — E1 (2026-09-08, sa liste de propositions).
// =====================================================================================
//
// ⛔ TROIS FONCTIONS, ET AUCUNE N'OUVRE QUOI QUE CE SOIT. C'est toute la difference avec
// `gfTelechargerFichier` : celle-la peut faire surgir un selecteur, ce qui est parfait quand
// quelqu'un vient d'appuyer sur un bouton, et inacceptable au demarrage de l'application.
//
// 📌 CE CHEMIN N'EXISTE QUE DEPUIS LA 2.31.77. Avant, ecrire un fichier demandait forcement
// d'ouvrir le selecteur d'Android ; depuis que l'autorisation porte sur un DOSSIER et qu'elle est
// persistante, l'application peut ecrire sans rien demander a personne. E1 etait impossible avant,
// et devient presque gratuit apres.

/** Y a-t-il un dossier retenu ? Repond sans rien ouvrir. Hors application native : non. */
async function gfDossierSauvegardeChoisi() {
  try {
    const SaveFile = gfPlugin("SaveFile");
    if (!SaveFile || !SaveFile.dossierChoisi) return { choisi: false, nom: null };
    const r = await SaveFile.dossierChoisi();
    return { choisi: !!(r && r.choisi), nom: (r && r.nom) || null };
  } catch (e) { return { choisi: false, nom: null }; }
}
window.gfDossierSauvegardeChoisi = gfDossierSauvegardeChoisi;

/**
 * Ecrit un fichier dans le dossier deja choisi, SANS selecteur et SANS notification.
 * ⛔ Rend `{sansDossier:true}` plutot que d'ouvrir quoi que ce soit quand aucun dossier n'est retenu.
 * ⚠️ Les echecs remontent en EXCEPTION : une sauvegarde automatique qui echoue en silence est une
 * fausse securite, et c'est tres exactement le defaut du fichier a 0 octet.
 */
async function gfEcrireDansDossierChoisi(nomFichier, blob, mime) {
  const SaveFile = gfPlugin("SaveFile");
  const Filesystem = gfPlugin("Filesystem");
  if (!SaveFile || !SaveFile.dossier || !Filesystem) return { sansDossier: true };
  if (!blob || !blob.size) throw new Error("Le contenu à enregistrer est vide : rien n'a été écrit.");
  const type = mime || blob.type || "application/json";
  const base64 = await gfBlobEnBase64(blob);
  if (!base64) throw new Error("Le contenu à enregistrer est vide : rien n'a été écrit.");
  const temporaire = "gf-auto-" + Date.now() + "-" + nomFichier;
  await Filesystem.writeFile({ path: temporaire, data: base64, directory: "CACHE" });
  const { uri } = await Filesystem.getUri({ path: temporaire, directory: "CACHE" });
  let r;
  try {
    r = await SaveFile.dossier({ nom: nomFichier, source: uri, mime: type, silencieux: true });
  } finally {
    try { await Filesystem.deleteFile({ path: temporaire, directory: "CACHE" }); } catch (e) {}
  }
  if (r && r.sansDossier) return { sansDossier: true };
  if (r && r.annule) return { annule: true };
  const ecrits = Number((r && r.octets) || 0);
  if (!ecrits) throw new Error("Le fichier écrit est vide (0 octet).");
  return { sansDossier: false, annule: false, octets: ecrits,
           relus: (r && r.relus != null) ? Number(r.relus) : -1 };
}
window.gfEcrireDansDossierChoisi = gfEcrireDansDossierChoisi;

/** Ne garde que les `garder` fichiers les plus recents commencant par `prefixe`. */
async function gfMenagerDossierSauvegardes(prefixe, garder) {
  try {
    const SaveFile = gfPlugin("SaveFile");
    if (!SaveFile || !SaveFile.menagerDossier) return { supprimes: [] };
    const r = await SaveFile.menagerDossier({ prefixe: prefixe, garder: garder });
    return { supprimes: (r && r.supprimes) || [], trouves: (r && r.trouves) || 0 };
  } catch (e) { return { supprimes: [], erreur: String((e && e.message) || e) }; }
}
window.gfMenagerDossierSauvegardes = gfMenagerDossierSauvegardes;

async function gfEnregistrerFichier(nomFichier, blob) {
  const Filesystem = gfPlugin("Filesystem");
  const Share = gfPlugin("Share");
  if (!Filesystem || !Share) throw new Error("Plugin Filesystem ou Share indisponible.");
  const base64 = await gfBlobEnBase64(blob);
  await Filesystem.writeFile({ path: nomFichier, data: base64, directory: "CACHE" });
  const { uri } = await Filesystem.getUri({ path: nomFichier, directory: "CACHE" });
  try {
    await Share.share({ url: uri, title: nomFichier, dialogTitle: "Enregistrer " + nomFichier });
  } catch (e) {
    // "Share canceled" : la personne a referme la feuille de partage. Ce n'est pas un echec, et
    // l'afficher en rouge comme tel etait trompeur (sa capture du 2026-08-10).
    if (/cancel/i.test(String((e && e.message) || e))) return { annule: true };
    throw e;
  }
  return { annule: false };
}

// ---------------------------------------------------------------------
//  5. Mise en route
// ---------------------------------------------------------------------
// Branche a DOMContentLoaded : config.js (donc `sb`) est charge a ce moment-la.
if (GF_NATIF) {
  document.addEventListener("DOMContentLoaded", () => {
    const App = gfPlugin("App");
    const SplashScreen = gfPlugin("SplashScreen");
    // ⭐ H3 (2026-09-11) — la taille de texte du TÉLÉPHONE, appliquée au texte de la WebView. Le calcul
    // vit dans config.js (`zoomTexteSysteme`, pure et testée) ; ici on ne fait que le brancher.
    // ⚠️ On relit la préférence à CHAQUE application, jamais une valeur retenue : la personne peut
    // l'avoir changée dans Android pendant que Solvea était en arrière-plan.
    const TextZoom = gfPlugin("TextZoom");
    window.gfAppliquerTailleSysteme = async function () {
      if (!TextZoom || typeof zoomTexteSysteme !== "function") return;
      try {
        const r = await TextZoom.getPreferred();
        const t = (typeof tailleActuelle === "function") ? tailleActuelle() : "normal";
        await TextZoom.set({ value: zoomTexteSysteme(r && r.value, t) });
      } catch (e) {}
    };
    window.gfAppliquerTailleSysteme();
    if (App) App.addListener("resume", () => window.gfAppliquerTailleSysteme());
    // ⭐ B2 (2026-09-11) : au retour dans Solvea, les dépenses tapées entre-temps sur le widget de saisie
    // rejoignent le compte. ⚠️ Seulement une fois le démarrage fini : avant, c'est la fin du démarrage
    // elle-même qui les reprend (morceau 18) — les deux ne se marchent jamais dessus.
    if (App) App.addListener("resume", () => {
      if (window.gfDemarrageFini && typeof window.gfIntegrerSaisiesWidget === "function") window.gfIntegrerSaisiesWidget();
    });
    // ⛔⛔ LE FILET, ET IL N'EST PAS FACULTATIF. Depuis `launchAutoHide: false`, plus RIEN côté Android
    // ne retire le splash : c'est le JavaScript qui doit le faire. Or le retrait normal vit ~200 lignes
    // plus bas, au bout de ce gestionnaire — si quoi que ce soit lève entre les deux, l'application
    // resterait sur son image de démarrage POUR TOUJOURS, sans un mot, et il faudrait la réinstaller.
    // 📌 Six secondes : bien au-delà de tout démarrage normal (mesuré à ~450 ms), assez court pour
    // qu'un écran figé reste un incident et pas un abandon. Retirer un splash déjà retiré ne fait rien.
    if (SplashScreen && SplashScreen.hide) {
      setTimeout(() => { try { SplashScreen.hide().catch(() => {}); } catch (e) {} }, 6000);
    }

    if (App) {
      // ⭐⭐ LE LIEN « ANNULER LA SUPPRESSION » ARRIVE ICI, PAS DANS LE NAVIGATEUR (2026-08-20, son
      // signalement : « le bouton annuler la suppression me ramene sur l'application et rien d'autre »).
      //
      // 🔴 LA CAUSE, ET ELLE ETAIT INVISIBLE DEPUIS LE CODE DE L'APP. `solvea.link` est declare en App
      // Link verifie (AndroidManifest.xml, `autoVerify="true"`) : TOUT lien `https://solvea.link/...`
      // ouvre l'APK au lieu du navigateur. Le lien d'annulation — `confirmation.html?annuler-suppression=`
      // — n'atteignait donc JAMAIS la page qui sait le traiter. Il tombait ici, ne correspondait ni au
      // widget ni au partage de profil, partait dans `gfTraiterRetourAuth()` qui n'y trouvait aucun
      // jeton d'authentification... et l'app s'ouvrait sur l'accueil. La demande restait programmee.
      //
      // ⛔⛔ CE QUE CE CHEMIN NE FAIT JAMAIS, ET IL FAUT QUE CA RESTE ECRIT ICI : il n'appelle QUE
      // `annuler-suppression`. Cette fonction-la ne sait faire qu'un `delete` sur une ligne de
      // `comptes_suppression` — meme appelee par erreur, en boucle, par n'importe qui, le pire qu'elle
      // puisse faire est d'ANNULER une suppression. Ni `supprimer-compte` ni `programmer-suppression`
      // ne doivent jamais etre atteintes par un lien : c'est exactement la faute du 2026-08-19, qui a
      // efface des donnees reelles a chaque demarrage de l'application.
      // ⛔ Et rien ne part sans jeton : un lien sans `annuler-suppression=<valeur>` ne declenche RIEN.
      // 🔴🔴 LA COURSE QUI RENDAIT LE CORRECTIF DE 2.12.53 INOPERANT AU DEMARRAGE A FROID, trouvee le
      // 2026-08-21 en relisant l'ORDRE d'execution, pas le routage lui-meme.
      //
      // Son signalement d'origine : « le bouton annuler la suppression me ramene sur l'application et
      // rien d'autre ». La 2.12.53 a bien amene le lien jusqu'ici — mais pas jusqu'a l'action.
      //
      // Ce qui se passait, dans l'ordre : natif.js est charge AVANT app.js, et `App.getLaunchUrl()`
      // resout en une microtache, donc quasi immediatement. `window.gfAnnulerSuppressionDepuisLien`,
      // elle, n'est posee par app.js qu'APRES `await exigerConnexion()` — un aller-retour reseau.
      // Le `typeof ... === "function"` etait donc FAUX, et le `if` retombait dans le vide : aucune
      // erreur, aucun message, l'app s'ouvrait sur l'accueil. La suppression restait programmee.
      // ⚠️ Et c'est le cas LE PLUS COURANT de tous : ouvrir le lien depuis sa boite mail, application
      // fermee. `tests/lien-annulation.test.js` ne pouvait pas le voir — il execute le routeur avec la
      // fonction deja definie, donc dans le seul monde ou la course n'existe pas.
      //
      // ⭐ LE REMEDE : on ne perd plus le jeton, on le MET DE COTE. `sessionStorage` est deja l'idiome
      // du projet pour ca (`gf_ajouter_ami`, 2.12.6), et il a un second merite ici — il survit au
      // detour par `index.html` quand il n'y a plus de session, si bien que le lien finit par agir
      // meme s'il faut se reconnecter d'abord. app.js vide cette case des qu'il est pret.
      // 📌 La fonction Edge `annuler-suppression`, elle, n'exige AUCUNE session quand on lui donne un
      // jeton : le report ne coute donc rien, il ne fait que decaler l'appel de quelques secondes.
      const CLE_ANNULATION = "gf_annuler_suppression";
      const routerLienProfond = async (url) => {
        if (!url) return false;
        // ⭐⭐ LE TICKET DE DÉPENSE DEPUIS L'EXTÉRIEUR — le « + » du widget ET l'appui long sur l'icône
        // (B6, 2026-09-11). Une seule adresse finale, `…/nouvelle-depense`, deux portes d'entrée.
        // 🔴 CE QUI NE MARCHAIT PAS, ET ÇA A ÉTÉ TROUVÉ EN BRANCHANT B6 : le « + » du widget n'était
        // traité que dans `appUrlOpen`, c'est-à-dire application DÉJÀ OUVERTE. Au démarrage à froid,
        // `getLaunchUrl()` passait par ce routeur, qui ne connaissait que l'annulation : l'app s'ouvrait
        // sur l'Accueil, sans ticket, et sans un mot. Même défaut que le lien d'annulation du 2026-08-21.
        // ⛔ ET ON N'OUVRE PAS LE TICKET TOUT DE SUITE À FROID : le cadenas est peut-être là, les données
        // pas encore arrivées. La demande est MISE DE CÔTÉ, et app.js l'honore à la TOUTE FIN de son
        // démarrage — « comme si on était dans l'application », sa phrase. `gfDemarrageFini` n'est posé
        // qu'à ce moment-là.
        // ⭐ `panier` (2026-09-20) : Mon Panier, l'application de courses, envoie le total de ses
        // courses ici. Meme porte que le widget et le raccourci, avec en plus un montant et un
        // libelle a poser dans le ticket — c'est l'utilisateur qui valide, jamais le lien.
        if (/\/\/(widget|raccourci|panier)\/nouvelle-depense/.test(url)) {
          // ⛔ Le pre-remplissage ne prend QUE ce qu'on sait lire : un montant positif et un libelle
          // court. Un lien qui arrive de l'exterieur n'ecrit rien d'autre dans l'application.
          let pre = null;
          try {
            const lien = new URL(url);
            const montant = parseFloat((lien.searchParams.get("montant") || "").replace(",", "."));
            const libelle = (lien.searchParams.get("libelle") || "").trim().slice(0, 60);
            if (montant > 0 || libelle) {
              pre = { montant: montant > 0 ? montant : 0, libelle, source: (lien.searchParams.get("source") || "").slice(0, 20) };
            }
          } catch (e) {}
          if (window.gfDemarrageFini && typeof window.gfOuvrirNouvelleDepense === "function") {
            window.gfOuvrirNouvelleDepense(pre);
          } else {
            // ⚠️ A froid, la demande attend la fin du demarrage (meme raison qu'au-dessus) : le
            // montant voyage avec elle, sinon le ticket s'ouvrirait vide.
            try { sessionStorage.setItem("gf_ouvrir_ticket", pre ? JSON.stringify(pre) : "1"); } catch (e) {}
          }
          return true;
        }
        if (/annuler-suppression=/.test(url)) {
          try {
            const u = new URL(url);
            const jeton = (u.searchParams.get("annuler-suppression") || "").trim();
            // ⛔ Pas de jeton, pas d'appel. Aucune valeur par defaut, jamais (regle A3).
            if (jeton) {
              if (typeof window.gfAnnulerSuppressionDepuisLien === "function") {
                await window.gfAnnulerSuppressionDepuisLien(jeton);
              } else {
                try { sessionStorage.setItem(CLE_ANNULATION, jeton); } catch (e) {}
              }
            }
          } catch (e) {}
          return true;
        }
        return false;
      };
      // Appelee par app.js juste apres avoir pose son gestionnaire : elle vide la case et rend le
      // jeton mis de cote, ou "" s'il n'y en a pas. ⚠️ Elle RETIRE avant de rendre — un jeton consomme
      // deux fois enverrait un second appel qui repondrait « ce lien n'est plus valable », et
      // l'utilisateur lirait un echec juste apres une reussite.
      window.gfJetonAnnulationEnAttente = function () {
        try {
          const j = sessionStorage.getItem(CLE_ANNULATION) || "";
          if (j) sessionStorage.removeItem(CLE_ANNULATION);
          return j;
        } catch (e) { return ""; }
      };
      // ⚠️ DEMARRAGE A FROID. `appUrlOpen` ne se declenche de facon fiable que si l'app tournait deja :
      // ouvrir le lien depuis une boite mail, application fermee, la lance — et l'evenement peut etre
      // parti avant que cet ecouteur existe. `getLaunchUrl()` rend l'adresse qui a servi au lancement :
      // c'est le seul moyen de ne pas perdre le geste dans ce cas-la, et c'est le cas le plus courant.
      if (typeof App.getLaunchUrl === "function") {
        App.getLaunchUrl().then((r) => routerLienProfond(r && r.url)).catch(() => {});
      }
      App.addListener("appUrlOpen", async (ev) => {
        if (!ev || !ev.url) return;
        if (await routerLienProfond(ev.url)) return;
        // Le bouton « + » du widget arrive ici, par le MEME schema que le retour de connexion
        // Google (declare une seule fois dans le manifeste). On le detourne avant l'authentification :
        // ce lien n'a rien a voir avec une session, et le passer a gfTraiterRetourAuth le ferait
        // analyser pour rien.
        // (Le « + » du widget et le raccourci B6 sont traités par `routerLienProfond` juste au-dessus,
        // à chaud comme à froid — il n'y a plus de second chemin ici.)
        // ⭐ App Links (2026-08-17) : le lien « Partager mon profil » (solvea.link/ajouter-ami.html)
        // arrive ici une fois le domaine verifie (voir AndroidManifest.xml + assetlinks.json), l'app
        // etant deja ouverte au premier plan — jamais un rechargement de page, donc jamais le detour
        // par sessionStorage que fait la version web (ajouter-ami.html).
        if (/\/ajouter-ami\.html(\?|$)/.test(ev.url)) {
          try {
            const u = new URL(ev.url);
            const pseudo = (u.searchParams.get("u") || "").trim();
            const tag = (u.searchParams.get("t") || "").trim();
            const identifiant = (pseudo && /^\d{4}$/.test(tag)) ? (pseudo + "#" + tag) : "";
            if (identifiant && typeof window.gfOuvrirAjoutAmiDepuisLien === "function") {
              window.gfOuvrirAjoutAmiDepuisLien(identifiant);
            }
          } catch (e) {}
          return;
        }
        const r = await gfTraiterRetourAuth(ev.url);
        // ⭐⭐⭐ L'ERREUR DU RETOUR OAUTH S'AFFICHE. C'ÉTAIT LE VRAI BUG DE LA LIAISON GOOGLE
        // (2026-08-24, son 4e signalement : « la liaison d'un compte Google ne marche toujours pas »).
        //
        // 🔴 CE QUI SE PASSAIT, ET AUCUNE DES TROIS TENTATIVES PRÉCÉDENTES NE POUVAIT LE TROUVER EN
        // RELISANT LE FLUX OAUTH : le code ci-dessous n'affichait le refus que `si typeof
        // afficherMessage === "function"`. Or `afficherMessage` n'existe QUE dans `index.html` et
        // `creer-compte.html` — les pages de connexion. La liaison, elle, se fait depuis `app.html`,
        // où cette fonction n'a jamais existé. La condition était donc TOUJOURS fausse : le serveur
        // répondait, le code recevait la réponse, la notait dans le Journal technique… et n'en disait
        // rien à l'écran. « Le navigateur se ferme et rien ne se passe », pendant trois versions.
        //
        // 📌 Son journal du 2026-08-24 donnait le message que personne ne voyait :
        //    « Identity is already linked to another user » — le compte Google visé appartient déjà à
        //    un AUTRE compte Solvea. Aucun correctif du flux ne pouvait lever ça ; ce qu'il fallait
        //    corriger, c'était le silence.
        //
        // ⛔ LE REFUS S'AFFICHE MAINTENANT SUR TOUTE PAGE, SANS EXCEPTION. `afficherMessage` sert
        // encore là où il existe (index.html et creer-compte.html le définissent, et il y place le
        // message dans le bandeau de la page) ; PARTOUT AILLEURS — app.html en tête, donc l'écran de
        // la liaison — c'est `gfToast` qui prend le relais : il vient de `config.js`, que toutes les
        // pages chargent. Plus aucun chemin où le message se perd.
        // ⚠️ Ne jamais remettre l'affichage entier derrière un `typeof … === "function"` unique : c'est
        // exactement la forme qui a mangé ce message pendant trois versions.
        // 📌 Un repère part aussi au Journal technique avec le message ANGLAIS d'origine : la traduction
        // est pour lui, la version brute est pour le diagnostic.
        if (r && r.ok === false && r.msg) {
          const texte = gfMessageLiaison(r.msg);
          if (typeof afficherMessage === "function") afficherMessage(texte, "err");
          else if (typeof gfToast === "function") gfToast(texte, 9000, "err");
          if (typeof noterErreur === "function") noterErreur("google-liaison", "Refus affiché : " + r.msg, "");
          // ⚠️ LE DRAPEAU « rouvrir les Paramètres » DOIT TOMBER ICI. En cas de succès, `app.html` est
          // rechargée et le retire elle-même à son démarrage (morceau 18) ; en cas de refus il n'y a
          // AUCUN rechargement — le drapeau resterait posé et rouvrirait les Paramètres au prochain
          // lancement, sans que personne n'ait rien demandé.
          try { sessionStorage.removeItem("gf_reglages_apres_auth"); } catch (e) {}
        }
      });

      // ⭐ Bouton (ou geste) retour Android — refait le 2026-08-08.
      //
      // AVANT : `if (canGoBack) window.history.back(); else minimizeApp();`. `canGoBack` est calcule par Capacitor
      // sur l'historique de la WEBVIEW, pas sur ce qui est ouvert a l'ecran. app.js poussait bien un niveau
      // d'historique par ouverture, mais il suffisait d'UN decalage (une fenetre ouverte sans etre armee, une
      // fermee sans etre observee) pour que ce drapeau soit faux : l'appui ne declenchait alors STRICTEMENT RIEN,
      // et la page restait affichee. C'est le defaut que l'utilisateur remontait depuis plusieurs versions.
      //
      // MAINTENANT : c'est l'app qui repond, pas l'historique. gfRetourAndroid() ferme ce qui est reellement
      // ouvert (menu ancre, fenetre modale, page plein ecran, dans cet ordre) et renvoie true s'il a ferme
      // quelque chose. C'est le schema recommande par Capacitor.
      //
      // Plus rien a fermer : premier appui = message, deuxieme appui dans les 2 s = mise en arriere-plan (et non
      // fermeture : fermer perdrait l'ecran en cours).
      // ⚠️ Bug remonte le 2026-08-08 : profil.html (et toute autre page secondaire — confidentialite.html,
      // suppression-compte.html) est atteinte par une VRAIE navigation (un <a href>, pas une SPA), et ne definit
      // pas `window.gfRetourAndroid` — seul app.html le fait. Le retour y tombait donc directement dans la
      // branche « plus rien a fermer » et minimisait l'app au lieu de revenir en arriere dans l'historique du
      // navigateur, qui existe pourtant bel et bien (on y est arrive par un lien). `history.back()` est le geste
      // standard pour une page qui n'a pas sa propre gestion de fermeture — utilise ici en repli generique, pour
      // CETTE page-ci et toute future page secondaire, sans avoir a cabler quoi que ce soit dans chacune d'elles.
      let dernierRetourRacine = 0;
      App.addListener("backButton", () => {
        try {
          if (typeof window.gfRetourAndroid === "function") {
            if (window.gfRetourAndroid()) { dernierRetourRacine = 0; return; }
          } else if (window.history.length > 1) {
            window.history.back();
            dernierRetourRacine = 0;
            return;
          }
        } catch (e) {}
        const maintenant = Date.now();
        if (maintenant - dernierRetourRacine < 2000) {
          dernierRetourRacine = 0;
          if (App.minimizeApp) App.minimizeApp();
          return;
        }
        dernierRetourRacine = maintenant;
        if (typeof window.gfToastQuitter === "function") window.gfToastQuitter();
        else if (App.minimizeApp) App.minimizeApp();
      });
    }

    // ⛔⛔ LE VERT EN DUR ET LE STYLE « DARK » EN DUR SONT PARTIS (2026-08-24, Capacitor 8 / API 36).
    // Ce bloc peignait la barre de statut en `#5C7A57` et forçait des icônes CLAIRES. Sur Android 16
    // le systeme impose le bord a bord : `setBackgroundColor` n'a plus aucun effet, la barre devient
    // TRANSPARENTE au-dessus de l'app, et des icones claires sur les six fonds CLAIRS de Solvea
    // rendaient l'heure et la batterie invisibles.
    // La decision vit maintenant dans `gfMajBarreSysteme()` (config.js), a cote de `appliquerApparence()`
    // qui l'appelle a chaque changement de fond ou de theme. Ici on ne fait plus que l'amorcer une
    // fois au demarrage : `appliquerApparence()` a tourne au chargement de config.js, AVANT que
    // `window.Capacitor.Plugins` ne soit forcement pret.
    gfMajBarreSysteme();
    // ⭐⭐⭐ LE SPLASH TIENT MAINTENANT JUSQU'ICI (2026-09-09, son signalement : « le chargement est
    // beaucoup trop lent, ça prend environ 2 s » sur un ECRAN SOMBRE ET VIDE).
    //
    // 🔴 CE QUE C'ETAIT : `launchAutoHide` valait vrai, avec `launchShowDuration: 500`. Le splash vert
    // s'effacait donc au bout d'une demi-seconde, alors que la WebView n'avait rien peint — et on
    // voyait la FENETRE ANDROID NUE (`android:background = @null`, donc le fond sombre du systeme sur
    // un telephone en mode sombre) pendant tout le reste du chargement. Une image de marque remplacee
    // par un rectangle noir : le pire des deux mondes.
    // ⛔ `launchAutoHide: false` desormais, et c'est CETTE LIGNE qui le retire — au moment ou
    // l'application est reellement la. `launchShowDuration` est ignore dans ce mode.
    if (SplashScreen && SplashScreen.hide) SplashScreen.hide().catch(() => {});

    // ⭐⭐⭐ LA TRACE DU DERNIER PLANTAGE NATIF, VERSEE DANS LE JOURNAL (2026-09-02, sa decision).
    //
    // 🔴 CE QUE CA REPARE : le Journal technique ne voyait QUE le JavaScript. Un plantage Java tue le
    // processus, donc `noterErreur()` n'existe deja plus au moment ou il faudrait l'appeler. La trace
    // est ecrite sur le disque par `PlantagePlugin` (cote Java) pendant que le processus meurt, et
    // c'est ICI, au demarrage SUIVANT, qu'on la ramasse — le seul moment ou l'application est vivante
    // pour la raconter.
    //
    // ⛔ `noterErreur` ET NON `noterRepere` : un plantage EST un probleme, il merite son bandeau. Et
    // c'est `noterErreur` qui remonte en base (`public.erreurs`, 2.31.29) : c'est tout l'objet de ce
    // chemin — a douze testeurs, personne ne pensera a envoyer une capture du Journal.
    // ⚠️ La ligne porte l'origine « plantage-natif », qui fait partie de la cle primaire cote base :
    // douze appareils qui plantent au meme endroit produisent UNE ligne avec douze occurrences.
    // ⛔ Enveloppe en entier, et sans `await` bloquant le demarrage : ramasser une trace de plantage
    // ne doit jamais pouvoir empecher l'application de s'ouvrir. Ce serait le comble.
    try {
      const Plantage = gfPlugin("Plantage");
      if (Plantage && Plantage.lire) {
        Plantage.lire()
          .then((r) => {
            const trace = (r && r.trace) || "";
            if (!trace) return; // le cas normal : rien a raconter
            // La premiere ligne utile de la pile fait le MESSAGE (donc la cle), le reste fait la
            // pile. Sans ca, deux plantages differents partageraient la meme ligne en base des que
            // leur premiere ligne se ressemble — ou l'inverse, un meme defaut en creerait mille.
            const lignes = trace.split("\n").filter((l) => l.trim());
            const tete = (lignes.find((l) => !/^fil=/.test(l.trim())) || lignes[0] || "plantage").trim();
            if (typeof window.gfNoterErreurNative === "function") {
              window.gfNoterErreurNative(tete.slice(0, 300), trace);
            }
          })
          .catch(() => {});
      }
    } catch (e) {}

    // Ouvrir une notification amene sur l'app : rien de special a faire au-dela de s'assurer
    // qu'on est bien sur l'ecran principal.
    const PN = gfPlugin("PushNotifications");
    if (PN) {
      // Canal Android obligatoire depuis Android 8 : sans lui, les notifications envoyees avec
      // channel_id="gf_rappels" (cf. supabase/functions/_shared/fcm.ts) ne s'afficheraient pas.
      if (PN.createChannel) {
        PN.createChannel({
          id: "gf_rappels",
          name: "Rappels de saisie",
          description: "Rappel quotidien et echeances a venir",
          importance: 4,
          visibility: 1,
        }).catch(() => {});
      }
      PN.addListener("pushNotificationActionPerformed", () => {
        if (!/app\.html$/.test(window.location.pathname)) window.location.href = "app.html";
      });
    }
  });
}

// ---------------------------------------------------------------------
//  WIDGET DE L'ECRAN D'ACCUEIL (2026-08-10)
// ---------------------------------------------------------------------
//  Un widget Android est du code natif : il n'a AUCUN acces au stockage de la WebView, ou vivent
//  toutes les donnees de l'app. Le seul pont possible est celui-ci — l'app pousse un resume DEJA
//  MIS EN FORME (des chaines pretes a afficher), le widget le lit.
//
//  Consequence a assumer, et a dire clairement : le widget montre l'etat de la derniere ouverture
//  de l'application. Il ne recalcule rien tout seul. C'est le fonctionnement de tous les widgets
//  de ce genre, et c'est ce qui evite de reveiller le telephone pour rien.
//
//  Sur le web, la fonction ne fait rien du tout : il n'y a pas de widget a nourrir.
async function gfPublierWidget(resume) {
  const Widget = gfPlugin("Widget");
  if (!Widget || !resume) return false;
  try {
    await Widget.publier({
      periode: String(resume.periode || "Solvea"),
      montant: String(resume.montant || "--"),
      budgetLibelle: String(resume.budgetLibelle || "Budget"),
      budgetPct: String(resume.budgetPct || ""),
      budgetValeur: Math.max(0, Math.min(100, Math.round(resume.budgetValeur || 0))),
      echeances: Array.isArray(resume.echeances) ? resume.echeances.slice(0, 4).map(String) : [],
      // La meme liste sans le montant : le widget la prefere des qu'il n'a que deux colonnes, ou la
      // ligne complete se ferait couper en plein milieu du prix (2026-08-13, son image 7).
      echeancesCourt: Array.isArray(resume.echeancesCourt) ? resume.echeancesCourt.slice(0, 4).map(String) : [],
      // La couleur du widget pose (2026-09-07, son image 7). Deux valeurs seulement : « clair » ou
      // « sombre ». Le cran « automatique » est resolu cote JS — le Java ne connait pas le fond de
      // l'application, et n'a aucune raison de l'apprendre.
      theme: resume.theme === "clair" ? "clair" : "sombre",
      // B2, le widget de saisie (2026-09-11) : le montant du jour en nombre, s'il est bien celui
      // d'aujourd'hui, et les listes du ticket pour la petite fenetre.
      montantValeur: Number.isFinite(+resume.montantValeur) ? +resume.montantValeur : 0,
      montantAujourdhui: resume.montantAujourdhui === true,
      deviseCode: String(resume.deviseCode || "EUR"),
      saisie: resume.saisie && typeof resume.saisie === "object" ? resume.saisie : {},
    });
    return true;
  } catch (e) {
    return false; // un widget qui ne se met pas a jour ne doit jamais gener l'application
  }
}

// B2 : les depenses enregistrees par le widget de saisie, pas encore reprises. Lire ne retire rien :
// l'application les enregistre d'abord, puis appelle gfRetirerSaisiesWidget avec leurs identifiants.
// Sur le web (pas de widget), une liste vide.
async function gfLireSaisiesWidget() {
  const Widget = gfPlugin("Widget");
  if (!Widget) return [];
  try {
    const r = await Widget.lireSaisies();
    return (r && Array.isArray(r.saisies)) ? r.saisies : [];
  } catch (e) {
    return [];
  }
}

async function gfRetirerSaisiesWidget(ids) {
  const Widget = gfPlugin("Widget");
  if (!Widget || !Array.isArray(ids) || !ids.length) return false;
  try {
    await Widget.retirerSaisies({ ids: ids.map(String) });
    return true;
  } catch (e) {
    return false;
  }
}

// ⭐ CHANGER L'ICONE DU LANCEUR (chantier « icones personnalisables », 2026-08-11).
//
//  Rappel de la contrainte, parce qu'elle revient a chaque fois : une icone Android est une
//  RESSOURCE COMPILEE dans l'APK. On ne peut pas en poser une choisie a l'execution, et surtout pas
//  une photo. Le seul mecanisme est une liste FIXE d'`activity-alias`, dont on n'active qu'un — cote
//  Java, dans IconePlugin. Ne pas repartir chercher autre chose : il n'y a rien d'autre.
//
//  Renvoie la cle appliquee, ou null si l'appel a echoue (web, ou variante inconnue cote natif).
async function gfChoisirIcone(cle) {
  const Icone = gfPlugin("Icone");
  if (!Icone) return null;
  try {
    const r = await Icone.choisir({ cle: String(cle || "defaut") });
    return (r && r.cle) || null;
  } catch (e) {
    return null;
  }
}

// L'icone reellement active, relue dans l'etat du systeme et non dans une preference : c'est la
// seule source qui ne peut pas mentir apres une reinstallation ou un effacement de donnees.
async function gfIconeActive() {
  const Icone = gfPlugin("Icone");
  if (!Icone) return null;
  try {
    const r = await Icone.disponibles();
    return (r && r.active) || null;
  } catch (e) {
    return null;
  }
}

// `gfReglerModeWidget` a ete supprime le 2026-08-11 avec le menu « Widget » de Personnalisation :
// chaque widget rend desormais le format sous lequel il a ete pose. Cote Android, `WidgetPlugin`
// n'expose plus `reglerMode` et `SolveaWidget` ne lit plus de preference de mode.

async function gfEffacerWidget() {
  const Widget = gfPlugin("Widget");
  if (!Widget) return;
  try { await Widget.effacer(); } catch (e) {}
}

// =====================================================================
//  LUMINOSITE DE L'ECRAN, LE TEMPS D'UN QR CODE (2026-08-21, son image 7)
// =====================================================================
//  Sa demande : « donne la possibilite de cliquer sur le QR code pour l'agrandir dans une fenetre et
//  d'illuminer la lumiere de l'ecran du telephone ».
//
//  Pourquoi ca compte, et pourquoi le fond blanc de la fenetre ne suffisait pas : un appareil photo
//  lit un QR par CONTRASTE. Un telephone en luminosite automatique dans une piece eclairee descend
//  souvent sous 20 % — le code est gris sur gris, le scan echoue, et rien n'explique pourquoi.
//
//  ⚠️ Sur le web (GF_NATIF faux), il n'y a AUCUNE API pour toucher la luminosite : ces deux fonctions
//  ne font rien, silencieusement, et c'est le bon comportement. Le fond blanc de `.qr-grand-box`, lui,
//  agit partout. ⛔ Ne pas chercher un contournement web : il n'en existe pas, et une fausse promesse
//  (« l'ecran va s'eclairer ») serait pire que rien.
//  ⛔ ET ON REND TOUJOURS CE QU'ON EMPRUNTE : `gfLuminositeRendre()` est appelee a la fermeture de la
//  fenetre ET par le retour Android. Un ecran laisse a fond vide la batterie sans que personne fasse
//  le lien avec l'ecran qui l'a demande.
async function gfLuminositeMax() {
  const L = gfPlugin("Luminosite");
  if (!L) return false;
  try { await L.maximum(); return true; } catch (e) { return false; }
}
async function gfLuminositeRendre() {
  const L = gfPlugin("Luminosite");
  if (!L) return false;
  try { await L.rendre(); return true; } catch (e) { return false; }
}
window.gfLuminositeMax = gfLuminositeMax;
window.gfLuminositeRendre = gfLuminositeRendre;

// =====================================================================
//  TRANSITION ENTRE LES PAGES (app.html <-> profil.html), 2026-08-10
// =====================================================================
//  Sa demande : « lorsqu'on clique sur le profil, l'ecran apparait
//  brusquement, mettre une transition egalement ».
//
//  Le profil n'est pas une fenetre de l'app mais une VRAIE page : la
//  navigation coupait net, sans le glissement qu'on a partout ailleurs.
//  Ce code vit ici parce que natif.js est le SEUL fichier deja charge par
//  les deux pages : aucune des deux n'a une ligne a ajouter, et une page
//  future en heritera sans rien faire.
//
//  Sens du geste, comme une pile d'ecrans : on avance vers la droite (la
//  page sortante part a gauche, l'entrante arrive de la droite), et on
//  revient vers la gauche. La profondeur est declaree ci-dessous plutot
//  que devinee : deux pages, autant l'ecrire.
//
//  Ce qui reste volontairement SANS animation : les redirections qui
//  suivent une deconnexion ou une suppression de compte. Ce ne sont pas
//  des allers-retours de navigation, et faire glisser un ecran qu'on
//  vient de vider n'aurait aucun sens.
//
//  ⭐ profil.html a disparu le 2026-08-17 (fusionné dans app.html) : plus aucun lien ne pointe
//  vers une profondeur 1, `jouerEntree()` continue de jouer l'entrée en fondu sur CHAQUE
//  chargement d'app.html/index.html (utile en soi, indépendant de profil.html), et `brancher()`
//  ne rencontre simplement plus jamais de clic vers une page à profondeur différente — inerte,
//  pas nuisible. Laissé tel quel : une page publique future (confidentialite.html et les autres
//  n'y sont déjà pas) pourrait un jour vouloir rejoindre ce système sans qu'il faille le réécrire.
(function () {
  // ⭐⭐ LES PAGES PUBLIQUES REJOIGNENT LE SYSTEME (2026-08-20, sa demande) : « ajouter une animation
  // de transition lorsqu'on clique sur Politique de confidentialite et Conditions d'utilisation, et
  // aussi pour le reste des boutons de la page d'accueil ». Elles s'ouvrent DEPUIS la connexion, donc
  // a une profondeur de 1 : on y entre par la droite, on en revient par la gauche.
  // ⚠️ Une page ne glisse que si elle CHARGE natif.js et porte un `.app` — les quatre ci-dessous le
  // font. En ajouter une ici sans l'un des deux ne casse rien (le code est defensif), mais ne
  // produirait qu'une demi-transition : sortie animee, entree brutale.
  var PROFONDEUR = {
    "app.html": 0, "index.html": 0,
    "creer-compte.html": 1, "confidentialite.html": 1, "conditions.html": 1, "suppression-compte.html": 1,
    // ⭐ confirmation.html (2026-08-21) : creer-compte.html y envoie maintenant directement
    // (`?email=…`, l'écran du code). Sans cette entrée, `quitterVers()` retombe sur un
    // `location.href` brut — pas un bug, juste une transition en moins.
    "confirmation.html": 1
  };
  var DUREE_SORTIE = 190; // doit rester <= a l'animation .page-sort (200 ms)
  var CLE_RETOUR = "gf_nav_arriere";

  function nomPage(url) {
    try {
      var chemin = new URL(url, window.location.href).pathname;
      var f = chemin.substring(chemin.lastIndexOf("/") + 1);
      return f || "app.html";
    } catch (e) { return ""; }
  }
  // Defensif : natif.js est aussi charge par des pages sans `.app` (et par les tests, contre un
  // document reduit). Aucune transition ne doit y planter quoi que ce soit.
  function app() {
    return typeof document.querySelector === "function" ? document.querySelector(".app") : null;
  }

  function jouerEntree() {
    var el = app(); if (!el) return;
    var arriere = false;
    try { arriere = sessionStorage.getItem(CLE_RETOUR) === "1"; sessionStorage.removeItem(CLE_RETOUR); } catch (e) {}
    el.classList.remove("page-sort", "page-entre", "vers-arriere");
    void el.offsetWidth;
    if (arriere) el.classList.add("vers-arriere");
    el.classList.add("page-entre");
    setTimeout(function () { el.classList.remove("page-entre", "vers-arriere"); }, 300);
  }

  // ⭐⭐ LA SORTIE, EXTRAITE POUR QUE LES BOUTONS S'EN SERVENT AUSSI (2026-08-20, sa demande) :
  // « ajouter une animation de transition lorsqu'on clique sur Politique de confidentialite et
  // Conditions d'utilisation, ET AUSSI POUR LE RESTE DES BOUTONS de la page d'accueil ».
  //
  // 🔴 CE QUI MANQUAIT, ET C'EST MESURE : le glissement de sortie n'etait branche que sur les `<a>`.
  // Un BOUTON qui navigue — « Tester sans compte », « Se connecter », la reprise d'un compte — posait
  // `window.location.href` sans un mot, et l'ecran changeait d'un coup sec. Verifie dans un vrai
  // navigateur : sur le lien, `.page-sort` se pose et `pageSortGauche` demarre ; sur le bouton, le
  // contexte etait detruit immediatement, donc aucune animation n'avait le temps de jouer.
  //
  // ⛔ ON N'ECRIT PAS UNE SECONDE ANIMATION POUR EUX. C'est la meme sortie, sortie de la delegation
  // de clic et rendue appelable : deux copies d'un mouvement, c'est la version « animation » du
  // composant qui redessine son habillage (regle d'interface n° 19, regle d'animation ①).
  //
  // ⚠️ Le repli n'est pas decoratif : sans `.app`, sans page connue, ou si la cible n'est pas dans
  // PROFONDEUR, on navigue TOUT DE SUITE. Une transition qui echoue ne doit jamais retenir quelqu'un
  // sur un ecran — mieux vaut un changement brusque qu'un bouton qui ne fait rien.
  /**
   * ⭐⭐⭐ `remplacer` — QUITTER SANS LAISSER DE PORTE DERRIÈRE SOI (2.32.30, sa remontée du 2026-09-19 :
   * « quand je fais retour android sur mon telephone ca fait une page blanche puis repart a la page de
   * connexion, ou ca m'affiche la page de l'onglet d'accueil du compte test pendant une fraction de
   * seconde et me fais revenir a la page de connexion »).
   *
   * 🔴 LES DEUX SYMPTÔMES ONT LA MÊME CAUSE, et le second la désigne : `app.html` restait dans
   * l'historique. Le bouton retour d'Android y revenait — mais on avait quitté le compte de test
   * (`gf_invite` retiré) et il n'y a pas de session : `exigerConnexion()` renvoie aussitôt vers la page
   * de connexion. On voyait donc l'accueil du compte de test une fraction de seconde, puis le rebond.
   * La « page blanche », c'est le même trajet quand l'app n'a pas le temps de peindre avant de rebondir.
   * ✅ UNE SORTIE DÉFINITIVE REMPLACE L'ÉTAPE au lieu d'en empiler une : le retour passe alors à ce
   * qu'il y avait AVANT, et ne repasse plus par un écran qui ne peut pas s'ouvrir.
   * ⚠️ Seules les sorties DE COMPTE s'en servent (quitter l'essai, revenir à la connexion, se
   * déconnecter). Une navigation ordinaire garde son historique : revenir en arrière doit marcher.
   */
  function quitterVers(url, options) {
    var remplacer = !!(options && options.remplacer);
    var aller = function () { if (remplacer) window.location.replace(url); else window.location.href = url; };
    var el = app();
    var cible = nomPage(url), ici = nomPage(window.location.href);
    if (!el || !(cible in PROFONDEUR) || !(ici in PROFONDEUR) || cible === ici) {
      aller();
      return;
    }
    var arriere = PROFONDEUR[cible] < PROFONDEUR[ici];
    if (arriere) { try { sessionStorage.setItem(CLE_RETOUR, "1"); } catch (err) {} }
    el.classList.remove("page-entre");
    if (arriere) el.classList.add("vers-arriere");
    el.classList.add("page-sort");
    setTimeout(function () {
      aller();
      // 🔴🔴 LE FILET, ET IL VIENT D'UN VRAI ÉCRAN NOIR (2026-08-24). `window.location.href` n'est PAS
      // une garantie de navigation : dans l'APK, Capacitor refuse toute URL dont l'hôte n'est ni celui
      // de l'app ni listé dans `server.allowNavigation` (`Bridge.launchIntent`, lignes 407-419 — il
      // lance un ACTION_VIEW externe et rend `true`, la WebView ne bouge pas). Une URL 404 ou un
      // schéma inconnu font pareil. Et comme la page vient d'être animée HORS de l'écran, il ne reste
      // que le fond de `body` : un écran vide, définitif, sans une erreur, sans un mot.
      //
      // ⭐ Si on est toujours là un instant plus tard, la navigation n'a pas eu lieu : on REMET la page.
      // Mieux vaut un bouton qui n'a rien fait — visible, réessayable — qu'un écran mort.
      // 📌 Si la navigation aboutit, le document se décharge et ce minuteur meurt avec lui : il ne
      // coûte donc rien dans le cas normal, et ne peut pas « annuler » un départ réussi.
      setTimeout(function () {
        var enc = app();
        if (enc) enc.classList.remove("page-sort", "vers-arriere");
      }, 1500);
    }, DUREE_SORTIE);
  }
  // Publiee sous le meme prefixe que le reste du pont : `index.html` et `creer-compte.html` ne
  // chargent pas app.js, c'est leur seul acces au socle d'animations.
  window.gfQuitterVers = quitterVers;

  function brancher() {
    jouerEntree();
    document.addEventListener("click", function (e) {
      var a = e.target.closest && e.target.closest("a[href]");
      if (!a) return;
      if (a.target === "_blank" || a.hasAttribute("download")) return;
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || (e.button && e.button !== 0)) return;
      var cible = nomPage(a.getAttribute("href"));
      if (!(cible in PROFONDEUR)) return;              // lien externe ou ancre : on ne touche a rien
      var ici = nomPage(window.location.href);
      if (!(ici in PROFONDEUR) || cible === ici) return;
      if (!app()) return;
      e.preventDefault();
      quitterVers(a.href);
    }, true);
    // Retour materiel d'Android ou du navigateur : la page revient du cache, `load` ne se rejoue pas.
    window.addEventListener("pageshow", function (ev) { if (ev.persisted) jouerEntree(); });
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", brancher);
  else brancher();
})();

// ============================================================================================
// ⭐⭐⭐ L'ACHAT DES ABONNEMENTS (2026-08-25) — SOLVEA FAMILY ET SOLVEA+
// ============================================================================================
//
// 🔴 SA CRAINTE, ET C'EST ELLE QUI DICTE L'ORDRE DES TROIS ETAPES CI-DESSOUS : « il ne faut surtout
// pas que les clients paient/achetent pour au final y avoir aucun changement au niveau de
// l'application, ce serait terrible ».
//
// ⛔ L'ORDRE N'EST PAS NEGOCIABLE, ET CHAQUE INVERSION A UN COUT PRECIS :
//   ① Play encaisse et rend un `purchaseToken` ;
//   ② le jeton part a `verifier-achat`, qui DEMANDE A GOOGLE et ecrit l'abonnement en base ;
//   ③ **et seulement alors** on acquitte aupres de Play.
// · Acquitter avant ② : on dit « c'est bon » a Google alors que rien n'est ouvert cote application.
// · Ne jamais acquitter : **Play REMBOURSE automatiquement au bout de trois jours** et l'abonnement
//   disparait, sans que personne comprenne pourquoi. C'est une regle de Play Billing, pas un bug —
//   et c'est la facon la plus silencieuse de realiser exactement ce qu'il redoute.
//
// ⚠️ RIEN ICI NE DECIDE DU PALIER. L'ecran ne fait que transmettre un jeton ; c'est le serveur qui
// lit la reponse de Google et ecrit `abonnements`. Un client modifie ne peut donc rien s'offrir.
//
// ⛔⛔ `forfait` (LE FORFAIT DE BASE : "mensuel" OU "annuel") EST OBLIGATOIRE DEPUIS QU'IL Y EN A DEUX
// (2026-08-25, sa demande : « peux-tu ajouter le fait de pouvoir s'abonner annuellement ? et offrir 2
// mois gratuits ? »). Tant que chaque produit n'avait qu'un forfait, le plugin prenait « le premier »
// sans ambiguïté possible. Avec deux, « le premier » est l'ordre dans lequel Google a répondu :
// quelqu'un qui lit 1,99 € et touche « Souscrire » pouvait être débité de 19,99 €. Le plugin REFUSE
// désormais un achat sans forfait plutôt que d'en choisir un — un refus se voit, un mauvais tarif se
// découvre sur le relevé bancaire.
async function gfAcheterAbonnement(produit, forfait) {
  const A = gfPlugin("Achat");
  if (!A) return { ok: false, msg: "Les abonnements ne sont disponibles que dans l'application Android." };
  if (!forfait) return { ok: false, msg: "Choisissez d'abord une formule : mensuelle ou annuelle." };
  let r;
  try { r = await A.acheter({ productId: produit, basePlanId: forfait }); }
  catch (e) {
    // ⭐⭐⭐ UN ACHAT REFUSE PAR PLAY CACHE SOUVENT UN ACHAT DEJA FAIT (2026-08-26, son signalement
    // repete : « je ne peux pas prendre l'abonnement solvea+, corrige, il faut que ce soit
    // possible »).
    //
    // 🔴 CE QUI SE PASSAIT. Son compte GOOGLE possede deja Solvea+ — achete depuis un AUTRE compte
    // Solvea, sur le meme telephone. Play refuse donc de le vendre une seconde fois (code 6 ou 7),
    // et c'est normal. Mais l'application s'arretait la, sur un message d'echec : elle n'allait
    // jamais DEMANDER a Play ce que ce compte possede deja.
    //
    // ⭐ On le demande maintenant. Deux issues, et les deux valent mieux qu'un echec sec :
    //   · l'abonnement lui appartient et n'est rattache a rien -> il est ACTIVE, sans repayer ;
    //   · il est deja rattache a un autre compte Solvea -> le serveur le dit en clair, avec la
    //     marche a suivre. C'est le message qui manquait.
    // ⛔ Ca ne contourne AUCUN paiement : `gfConfirmerAchat` passe par `verifier-achat`, qui demande
    // a Google. Un jeton qu'on ne possede pas ne donne rien.
    const rattrape = await gfRattraperAchatExistant(produit);
    if (rattrape) return rattrape;
    return { ok: false, msg: (e && e.message) || "L'achat n'a pas abouti." };
  }

  // Une annulation n'est pas une erreur : quelqu'un a change d'avis, on ne lui met pas un bandeau rouge.
  if (r && r.annule) return { ok: false, annule: true };
  // Un paiement en attente (virement, carte prepayee) : on le DIT, plutot que de laisser l'ecran figé.
  if (r && r.enAttente) {
    return { ok: false, msg: "Votre paiement est en attente de validation. L'abonnement s'activera tout seul dès que Google l'aura confirmé." };
  }
  if (!r || !r.purchaseToken) {
    // Meme rattrapage quand Play repond sans jeton : l'achat n'a pas eu lieu, mais il existait
    // peut-etre deja.
    const rattrape = await gfRattraperAchatExistant(produit);
    if (rattrape) return rattrape;
    return { ok: false, msg: "L'achat n'a pas abouti." };
  }
  return await gfConfirmerAchat(r.productId || produit, r.purchaseToken, r.acquitte);
}

// ⭐ « CE COMPTE GOOGLE POSSEDE-T-IL DEJA CET ABONNEMENT ? » — appelee uniquement apres un achat
// refuse. Rend `null` s'il n'y a rien a rattraper, pour que l'appelant garde son message d'origine.
// ⚠️ On ne regarde QUE le produit demande : rattacher un abonnement que la personne n'a pas cherche
// a acheter serait une surprise, meme agreable.
async function gfRattraperAchatExistant(produit) {
  const A = gfPlugin("Achat");
  if (!A) return null;
  let liste = [];
  try { const r = await A.restaurer(); liste = (r && r.achats) || []; } catch (e) { return null; }
  const trouve = liste.find((a) => a && a.productId === produit);
  if (!trouve) return null;
  const c = await gfConfirmerAchat(trouve.productId, trouve.purchaseToken, trouve.acquitte);
  // ⛔ ON REND LE MESSAGE DU SERVEUR, PAS CELUI DE PLAY. « Cet achat est deja rattache a un autre
  // compte Solvea. Connectez-vous avec ce compte-la. » dit ce qu'il faut faire ; « code 6 » non.
  if (c && (c.ok || c.msg)) return c;
  return null;
}

// ② et ③, extraites : `gfRestaurerAchats()` a besoin exactement des memes.
async function gfConfirmerAchat(produit, jeton, dejaAcquitte) {
  let data, error;
  try {
    const rep = await sb.functions.invoke("verifier-achat", { body: { productId: produit, purchaseToken: jeton } });
    data = rep.data; error = rep.error;
  } catch (e) { error = e; }

  if (error || !data || !data.ok) {
    // ⛔ UN ECHEC NE S'ANNONCE JAMAIS COMME UN SUCCES (REGLE.md B1/C1). La personne a paye : elle doit
    // savoir que l'activation n'a pas abouti, et qu'elle n'aura pas a repayer.
    // ⚠️ ET ON N'ACQUITTE PAS : sans acquittement Google rembourse a J+3, ce qui est la bonne issue
    // quand on n'a pas su ouvrir l'abonnement. Acquitter ici garderait l'argent sans rien donner.
    const msg = (typeof messageFonctionEdge === "function" ? await messageFonctionEdge(error, data) : "")
      || "L'abonnement n'a pas pu être activé. Rouvrez l'application : rien n'est perdu et vous ne serez pas débité une seconde fois.";
    return { ok: false, msg: msg };
  }

  // ③ Le serveur a confirme ET ecrit. On peut acquitter.
  if (!dejaAcquitte) {
    try { await gfPlugin("Achat").acquitter({ purchaseToken: jeton }); } catch (e) { /* réessayé au prochain démarrage */ }
  }
  return { ok: true, palier: data.palier, expire: data.expire };
}

// ⭐ LES ACHATS DEJA FAITS, RELUS A CHAQUE DEMARRAGE. Sans cet appel, quelqu'un qui change de
// telephone, reinstalle l'application ou vide ses donnees a paye un abonnement que plus rien ne
// retrouve — et l'ecran lui redemanderait de payer. Google garde l'achat ; c'est a nous d'aller le lire.
// ⛔ PAS DERRIERE UN BOUTON « Restaurer mes achats » : un bouton suppose qu'on sache qu'il existe, et
// c'est justement ce que quelqu'un qui vient de perdre son abonnement ne sait pas.
async function gfRestaurerAchats() {
  const A = gfPlugin("Achat");
  if (!A) return null;
  let liste = [];
  try { const r = await A.restaurer(); liste = (r && r.achats) || []; } catch (e) { return null; }
  let dernier = null;
  for (const a of liste) {
    // ⚠️ On repasse par le SERVEUR pour chacun : c'est lui qui verifie que l'achat est encore vivant.
    // Un abonnement resilie il y a deux mois est encore rendu par `restaurer()` — le croire ouvrirait
    // des fonctions payantes a quelqu'un qui ne paie plus.
    const r = await gfConfirmerAchat(a.productId, a.purchaseToken, a.acquitte);
    if (r && r.ok) dernier = r;
  }
  return dernier;
}

// ⭐⭐ LES VRAIS PRIX, DEMANDÉS À GOOGLE (2026-08-25).
//
// 🔴 POURQUOI ON NE PEUT PAS SE CONTENTER DES PRIX ÉCRITS DANS L'ÉCRAN. La page des paliers annonce
// « 1,99 € » et « 4,99 € » en dur. Ces deux nombres ne sont vrais QUE dans la zone euro, et seulement
// tant que le réglage fiscal de la Play Console fait afficher des prix TTC. En Crôatie c'est 2,49 €,
// au Danemark des couronnes, en Côte d'Ivoire des francs CFA — et Play affichera SON chiffre au moment
// de payer. Un écran qui annonce un prix et un paiement qui en prélève un autre, c'est la version
// silencieuse de ce qu'il redoute : « payer pour au final y avoir aucun changement ».
//
// 📌 CE QUE ÇA REND, ET CE QUE ÇA NE REND PAS. Une table `{ produit: { forfait: {prix, periode} } }`.
// Rien de plus : pas de jeton d'offre, pas de décision de palier. Le jeton reste dans le plugin, où
// `choisirOffre()` garantit que le prix affiché et le prix facturé sortent de la MÊME offre.
//
// ⚠️ RENVOIE `null` HORS DE L'APPLICATION ANDROID, et l'appelant garde alors ses prix écrits. Un `{}`
// vide serait lu comme « Google dit qu'il n'y a aucun prix » (REGLE.md C1 : zéro n'est pas un succès).
async function gfPrixAbonnements() {
  const A = gfPlugin("Achat");
  if (!A) return null;
  let liste;
  try { const r = await A.produits(); liste = r && r.produits; }
  catch (e) { return null; }
  if (!Array.isArray(liste) || !liste.length) return null;
  const table = {};
  for (const p of liste) {
    if (!p || !p.productId || !Array.isArray(p.forfaits)) continue;
    const par = {};
    for (const f of p.forfaits) {
      if (!f || !f.forfait || !f.prix) continue;
      // ⭐ `prix` EST LE PRIX RECURRENT, PAS CELUI DE LA PREMIERE PHASE. Avec un essai gratuit, la
      // premiere phase vaut zero : l'afficher annoncerait « 0,00 € par mois » sur un abonnement a
      // 4,99 €. Le plugin rend donc deja la phase recurrente ici, et l'essai a part.
      par[f.forfait] = {
        prix: f.prix, periode: gfPeriodeEnMots(f.periode), iso: f.periode || "",
        // `micros` sert a comparer le mensuel et l'annuel, jamais a s'afficher.
        micros: +f.micros || 0,
        // ⚠️ `null` QUAND IL N'Y A PAS D'ESSAI, jamais un objet vide : un objet vide se lit comme
        // « il y a un essai, de duree inconnue » et l'ecran annoncerait un essai inexistant.
        essai: f.essai && f.essai.periode ? {
          gratuit: !!f.essai.gratuit,
          prix: f.essai.prix || "",
          duree: gfDureeEnMots(f.essai.periode, +f.essai.cycles || 1),
        } : null,
      };
    }
    if (Object.keys(par).length) table[p.productId] = par;
  }
  return Object.keys(table).length ? table : null;
}

// ⭐ LA DUREE D'UN ESSAI SE DIT EN JOURS OU EN MOIS, PAS EN « PERIODES ». `gfPeriodeEnMots` répond à
// « tous les combien ? » (« par mois ») ; ici la question est « pendant combien de temps ? », et la
// réponse n'a pas la même forme. Deux besoins, deux fonctions — les confondre donnerait « gratuit
// par semaine ».
// ⚠️ `cycles` compte, et il vaut 1 par défaut : un essai peut durer PLUSIEURS cycles (Play permet
// « 3 mois à moitié prix »). Sans lui, on annoncerait un mois là où il y en a trois.
function gfDureeEnMots(iso, cycles) {
  const m = /^P(\d+)([DWMY])$/.exec(String(iso || ""));
  if (!m) return "";
  const n = (+m[1]) * (cycles > 0 ? cycles : 1);
  // 📌 Les semaines se disent en JOURS : « 14 jours » se comprend d'un coup, « 2 semaines » demande
  // une conversion. C'est aussi le mot qu'il a employé lui-même.
  if (m[2] === "W") return (n * 7) + " jours";
  const un = { D: ["jour", "jours"], M: ["mois", "mois"], Y: ["an", "ans"] }[m[2]];
  if (!un) return "";
  return n + " " + (n === 1 ? un[0] : un[1]);
}

// La période de facturation arrive en ISO 8601 (« P1M », « P1Y ») : c'est la forme que Play rend, pas
// un choix. Elle ne s'affiche pas telle quelle — personne ne lit « P1Y ».
function gfPeriodeEnMots(iso) {
  const m = /^P(\d+)([DWMY])$/.exec(String(iso || ""));
  if (!m) return "";
  const n = +m[1];
  const un = { D: ["par jour", "jours"], W: ["par semaine", "semaines"], M: ["par mois", "mois"], Y: ["par an", "ans"] }[m[2]];
  if (!un) return "";
  return n === 1 ? un[0] : "tous les " + n + " " + un[1];
}

window.gfAcheterAbonnement = gfAcheterAbonnement;
window.gfRestaurerAchats = gfRestaurerAchats;
// ⭐⭐ RESILIER SE FAIT CHEZ GOOGLE, ET ON Y EMMENE LA PERSONNE (2026-08-25, sa question : « peut-on
// annuler un abonnement depuis l'application ou il faut absolument passer par Google Play Store ? »).
//
// 🔴 LA REPONSE EST : PAS DEPUIS L'APPLICATION, ET CE N'EST PAS UN CHOIX DE CONCEPTION. Play Billing
// n'expose aucune API d'annulation — Google est le vendeur, le contrat lie la personne a lui. Un
// bouton « Resilier » ici ne pourrait que MENTIR sur ce qu'il fait.
//
// ⛔ MAIS NE RIEN METTRE N'EST PAS UNE OPTION NON PLUS. Un abonnement a renouvellement automatique
// dont on ne dit pas ou se desabonner est exactement ce que la loi et le reglement Play refusent — et
// la mention affichee sous les boutons d'achat promet « resiliable a tout moment dans Google Play ».
// Sans ce lien, cette promesse demanderait de chercher.
//
// 📌 `sku` + `package` ouvrent la page DE CET abonnement, pas la liste : deux gestes de moins pour
// quelqu'un qui en a plusieurs. Sans `produit`, on retombe sur la liste, ce qui reste juste.
async function gfOuvrirAbonnementPlay(produit) {
  const base = "https://play.google.com/store/account/subscriptions";
  const url = produit
    ? base + "?sku=" + encodeURIComponent(produit) + "&package=" + encodeURIComponent(GF_APP_ID)
    : base;
  const Browser = gfPlugin("Browser");
  // ⚠️ `Browser.open` PLUTOT QUE `window.location` : une navigation hors du domaine de l'app est
  // refusee par Capacitor (le piege de `creer-compte.html`, 21e session). Hors application, l'onglet
  // ordinaire fait l'affaire.
  if (Browser) { try { await Browser.open({ url: url }); return true; } catch (e) { /* on retombe */ } }
  try { window.open(url, "_blank", "noopener"); return true; } catch (e) { return false; }
}

window.gfPrixAbonnements = gfPrixAbonnements;
window.gfOuvrirAbonnementPlay = gfOuvrirAbonnementPlay;
window.gfAuthOccupee = gfAuthOccupee;

// ---------------------------------------------------------------------
//  6. Les annonces RECOMPENSEES (2026-08-26, chantier n° 2)
// ---------------------------------------------------------------------
// Sa decision, posee au depart : pubs RECOMPENSEES uniquement, aucun bandeau, CMP UMP obligatoire.
//
// 🔴 CE PONT NE DONNE JAMAIS DE NACRES. Il dit seulement « l'annonce a ete regardee jusqu'a la
// recompense » ou « non ». Le credit, lui, est ecrit en base par `reclamerPubRecompensee()`
// (morceau 16), sous le plafond tenu par la contrainte de `pubs_vues`. Un pont qui crediterait
// serait un pont qu'il suffit d'appeler depuis la console du navigateur.
//
// ⚠️ SUR LE WEB, TOUT REND `false` SANS ERREUR. Le plugin `Pub` n'existe que dans l'APK : sur le
// site, les trois fonctions repondent « pas d'annonce », et l'ecran n'affiche donc rien. C'est le
// comportement voulu — pas de publicite sur solvea.link.

/** L'etat du consentement, retenu pour la session. `null` = la question n'a pas encore ete posee. */
let _gfPubEtat = null;

/**
 * Pose la question du consentement (si elle doit l'etre) et demarre le SDK si la reponse le permet.
 * A appeler UNE fois au demarrage. Rend `{peutServir, choixDisponible}`.
 *
 * ⛔ RIEN NE DOIT PRECEDER CET APPEL cote annonces. `charger()` refuse tant qu'il n'a pas eu lieu :
 * c'est volontaire, et c'est ce qui empeche une annonce de partir sur un consentement suppose.
 */
async function gfConsentementPub() {
  const Pub = gfPlugin("Pub");
  if (!Pub) { _gfPubEtat = { peutServir: false, choixDisponible: false }; return _gfPubEtat; }
  try {
    const r = await Pub.consentement();
    _gfPubEtat = {
      peutServir: !!(r && r.peutServir),
      choixDisponible: !!(r && r.choixDisponible),
      erreur: (r && r.erreur) || ""
    };
  } catch (e) {
    // 🔴 ON NE SUPPOSE JAMAIS « OUI » SUR UNE ERREUR : sans reponse de la CMP, il n'y a pas de
    // consentement, donc pas d'annonce. Retomber sur `true` serait l'infraction qu'on evite.
    _gfPubEtat = { peutServir: false, choixDisponible: false, erreur: String((e && e.message) || e) };
  }
  return _gfPubEtat;
}

/** Ce que la CMP a repondu, sans la redemander. `false` avant le premier appel. */
function gfPubAutorisee() { return !!(_gfPubEtat && _gfPubEtat.peutServir); }
/** Le formulaire de choix est-il disponible ? Decide si le bouton de reglages s'affiche. */
function gfPubChoixDisponible() { return !!(_gfPubEtat && _gfPubEtat.choixDisponible); }

/**
 * Prepare une annonce a l'avance. Rend `true` si elle est prete.
 * ⚠️ SEPARE DE `gfMontrerPub()` : une annonce met une a trois secondes a arriver. Charger au moment
 * du clic laisserait le bouton mort pendant ce temps, et un bouton qui promet une recompense sans
 * rien ouvrir est pire que pas de bouton.
 */
async function gfChargerPub() {
  const Pub = gfPlugin("Pub");
  if (!Pub || !gfPubAutorisee()) return false;
  try { const r = await Pub.charger(); return !!(r && r.pret); }
  catch (e) { return false; }
}

/**
 * Montre l'annonce. Rend `{recompense:bool, motif:string}` — `motif` n'est rempli qu'en cas d'echec,
 * pour que l'ecran puisse le DIRE au lieu de ne rien faire.
 * ⛔ `recompense:false` veut dire « fermee trop tot » : on ne credite rien, et on ne le reproche pas.
 */
async function gfMontrerPub() {
  const Pub = gfPlugin("Pub");
  if (!Pub) return { recompense: false, motif: "Les annonces n'existent que dans l'application." };
  try {
    const r = await Pub.montrer();
    return { recompense: !!(r && r.recompense), motif: "" };
  } catch (e) {
    return { recompense: false, motif: String((e && e.message) || e) };
  }
}

/**
 * Rouvre le formulaire de choix.
 * ⛔ OBLIGATOIRE, PAS OPTIONNEL : un consentement doit se retirer aussi facilement qu'il se donne.
 * C'est le bouton de « Parametres et confidentialite ».
 */
async function gfModifierChoixPub() {
  const Pub = gfPlugin("Pub");
  if (!Pub) return false;
  try {
    const r = await Pub.modifierChoix();
    // Le choix vient peut-etre de changer : on met a jour l'etat retenu, sinon l'ecran continuerait
    // de proposer des annonces a quelqu'un qui vient de les refuser.
    if (_gfPubEtat) _gfPubEtat.peutServir = !!(r && r.peutServir);
    return !!(r && r.ok);
  } catch (e) { return false; }
}

window.gfConsentementPub = gfConsentementPub;
window.gfPubAutorisee = gfPubAutorisee;
window.gfPubChoixDisponible = gfPubChoixDisponible;
window.gfChargerPub = gfChargerPub;
window.gfMontrerPub = gfMontrerPub;
window.gfModifierChoixPub = gfModifierChoixPub;

// ============================================================================================
//  ⭐⭐⭐ LES MISES À JOUR SANS RÉINSTALLER (2026-09-14, sa demande : « que ça mette à jour, sans
//  réinstaller, que les corrections arrivent toutes seules à l'ouverture de l'app, sans
//  retélécharger l'APK, tant que rien ne change côté Android »).
// ============================================================================================
//
//  📌 LE PRINCIPE, EN UNE PHRASE : le site publie, à chaque version, le MÊME paquet web que l'APK
//  embarque (dist/maj/<version>/, build-site.mjs) avec un manifeste d'empreintes ; l'application
//  installée le lit à l'ouverture, télécharge ce qui a changé, le vérifie octet par octet, et
//  Capacitor sert ce dossier au démarrage suivant — même origine `https://localhost`, donc rien de
//  perdu (localStorage, caches, session). Le Java est dans MajPlugin.java, et il y a un filet : une
//  version qui ne finit pas de démarrer est refusée au lancement suivant.
//
//  ⛔ « TANT QUE RIEN NE CHANGE CÔTÉ ANDROID », C'EST LE CONTRAT NATIF : le manifeste dit quelle
//  version du contrat il attend, l'APK dit laquelle il fournit (MajPlugin.NATIF_VERSION). Différents
//  → on ne télécharge rien, l'APK se mettra à jour par le chemin normal. `scripts/natif-contrat.mjs`
//  et `tests/natif-contrat.test.js` obligent à incrémenter le contrat dès qu'un fichier Android bouge.

const GF_MAJ_BASE = "https://solvea.link/maj";

/** « 2.31.144 » > « 2.31.143 » ? Trois nombres, jamais une comparaison de chaînes (« 2.31.9 » < « 2.31.10 »). */
function gfVersionPlusRecente(a, b) {
  const n = (v) => String(v || "").split(".").map((x) => parseInt(x, 10) || 0);
  const [x, y] = [n(a), n(b)];
  for (let i = 0; i < 3; i++) { if ((x[i] || 0) !== (y[i] || 0)) return (x[i] || 0) > (y[i] || 0); }
  return false;
}

/**
 * ⭐ PURE ET TESTÉE : faut-il télécharger ce manifeste ? Elle ne touche à rien, elle répond.
 * @param courante  la version du paquet qui tourne (version.json du paquet servi)
 * @param etat      ce que MajPlugin.etat() rend : {natif, active, essai, refusee, confirmee}
 * @param manifest  ce que le site publie : {version, natif, fichiers:[{chemin, sha256, taille}]}
 * @returns {action:"telecharger"|"rien", raison}
 */
function gfDecisionMiseAJour(courante, etat, manifest) {
  if (!manifest || typeof manifest !== "object" || !Array.isArray(manifest.fichiers) || !manifest.fichiers.length)
    return { action: "rien", raison: "manifeste illisible" };
  if (!/^\d+\.\d+\.\d+$/.test(String(manifest.version || ""))) return { action: "rien", raison: "version illisible" };
  if (!etat || typeof etat.natif !== "number") return { action: "rien", raison: "état natif inconnu" };
  if (Number(manifest.natif) !== etat.natif) return { action: "rien", raison: "contrat natif différent (" + manifest.natif + " ≠ " + etat.natif + ") : il faut un nouvel APK" };
  if (!gfVersionPlusRecente(manifest.version, courante)) return { action: "rien", raison: "déjà à jour (" + courante + ")" };
  if (manifest.version === etat.refusee) return { action: "rien", raison: "version refusée au démarrage précédent" };
  if (manifest.version === etat.active) return { action: "rien", raison: "déjà téléchargée, active au prochain démarrage" };
  const mauvais = manifest.fichiers.find((f) => !f || typeof f.chemin !== "string" || !/^[0-9a-f]{64}$/.test(String(f.sha256 || ""))
    || f.chemin.includes("..") || f.chemin.startsWith("/"));
  if (mauvais) return { action: "rien", raison: "manifeste douteux : " + JSON.stringify(mauvais) };
  return { action: "telecharger", raison: manifest.version + " remplace " + courante };
}

let _gfMajDerniere = 0;
let _gfMajEnCours = false;

async function gfVerifierMiseAJour(origine) {
  const Maj = gfPlugin("Maj");
  if (!GF_NATIF || !Maj || _gfMajEnCours) return null;
  // Une fois par heure au retour dans l'application, toujours à l'ouverture.
  if (origine === "resume" && Date.now() - _gfMajDerniere < 3600000) return null;
  _gfMajEnCours = true;
  _gfMajDerniere = Date.now();
  // ⭐⭐ CHAQUE ISSUE EST ÉCRITE AU JOURNAL TECHNIQUE (2026-09-14, sa phrase : « la maj automatique ne
  // fonctionne pas »). Jusqu'ici la fonction rendait sa décision à personne : rien — ni sur son
  // téléphone, ni dans la table `erreurs` — ne permettait de savoir POURQUOI une version n'arrivait
  // pas. « Déjà à jour » n'est pas noté : c'est le cas normal, à chaque ouverture.
  const noter = (r) => { try { if (typeof noterErreur === "function") noterErreur("maj", origine + " : " + JSON.stringify(r), ""); } catch (e) {} return r; };
  try {
    const etat = await Maj.etat();
    // ⭐ LE PAQUET QUI TOURNE A FINI DE DÉMARRER : c'est la confirmation que le filet attend.
    // ⛔ AVANT TOUT, ET MÊME SANS RÉSEAU (NATIF_VERSION 2) : le filet natif refuse, au démarrage
    // suivant, toute version servie qui n'a pas dit « je suis là ». Une première ouverture hors ligne
    // après une mise à jour ne doit pas la faire refuser — la confirmation prouve que CE JavaScript
    // a démarré, elle n'a pas besoin du réseau.
    if (etat && etat.essai) { try { await Maj.confirmer(); } catch (e) { noter({ action: "erreur", raison: "confirmer : " + ((e && e.message) || e) }); } }
    if (!navigator.onLine) return { action: "rien", raison: "hors ligne" };
    const courante = await fetch("./version.json", { cache: "no-store" }).then((r) => r.json()).then((j) => j.version).catch(() => null);
    if (!courante) return noter({ action: "rien", raison: "version courante illisible" });
    // ⚠️ PAR LE JAVA, PAS PAR fetch() : depuis l'origine `https://localhost`, un fetch vers solvea.link
    // est inter-origines et le site ne pose pas d'en-tête CORS. Le manifeste passe donc par le même
    // chemin que les fichiers (HttpURLConnection), sans cache.
    const manifest = await Maj.manifeste({ url: GF_MAJ_BASE + "/manifest.json" })
      .then((r) => JSON.parse(r && r.texte || "null"))
      .catch((e) => { noter({ action: "erreur", raison: "manifeste : " + ((e && e.message) || e) }); return null; });
    const decision = gfDecisionMiseAJour(courante, etat, manifest);
    if (decision.action !== "telecharger") return /déjà à jour/.test(decision.raison || "") ? decision : noter(decision);
    const r = await Maj.telecharger({ base: GF_MAJ_BASE, version: manifest.version, fichiers: manifest.fichiers });
    await Maj.activer({ dossier: r.dossier, version: manifest.version });
    // ⭐ SI ON VIENT D'OUVRIR ET QU'ON N'A ENCORE RIEN TOUCHÉ, ON RECHARGE TOUT DE SUITE : la mise à
    // jour arrive « à l'ouverture », comme il le demande, au prix d'un second écran de démarrage.
    // Sinon — une saisie en cours, un écran ouvert — elle attend le prochain lancement, et on le dit.
    const tot = (performance.now() < 8000) && !window._gfTouche;
    if (tot) { try { noter({ action: "rechargee", version: manifest.version }); await Maj.recharger(); return { action: "rechargee", version: manifest.version }; } catch (e) {} }
    if (typeof gfToast === "function") gfToast("Solvea " + manifest.version + " est prête · elle s'appliquera à la prochaine ouverture.", 6000, "ok");
    return noter({ action: "activee", version: manifest.version });
  } catch (e) {
    return noter({ action: "erreur", raison: String((e && e.message) || e) });
  } finally {
    _gfMajEnCours = false;
  }
}

// ⚠️ Dans DOMContentLoaded, comme le reste du pont : à l'analyse du script, `Capacitor.Plugins` n'est
// pas forcément prêt (voir gfMajBarreSysteme, plus haut).
if (GF_NATIF) document.addEventListener("DOMContentLoaded", () => {
  document.addEventListener("pointerdown", () => { window._gfTouche = true; }, { once: true, passive: true });
  // Après le démarrage — jamais avant : le démarrage a la priorité sur tout, et une reprise du
  // widget ou une ouverture de ticket ne doit pas se battre avec un téléchargement.
  const attendre = setInterval(() => {
    if (!window.gfDemarrageFini) return;
    clearInterval(attendre);
    setTimeout(() => gfVerifierMiseAJour("demarrage"), 1500);
  }, 250);
  setTimeout(() => clearInterval(attendre), 60000);
  const App = gfPlugin("App");
  if (App && App.addListener) App.addListener("resume", () => { if (window.gfDemarrageFini) gfVerifierMiseAJour("resume"); });
});

window.gfVerifierMiseAJour = gfVerifierMiseAJour;
window.gfDecisionMiseAJour = gfDecisionMiseAJour;
